/**
 * @module public/js/guidance/guidanceClient
 * @summary Runtime renderer for targeted guidance and onboarding campaigns.
 */

let guidanceStarted = false;
const GUIDANCE_POSITION_MARGIN = 12;
const DEFAULT_GUIDANCE_POSITIONS = Object.freeze({
  announcement_center: Object.freeze({ x: 1.5, y: 88 }),
  banner: Object.freeze({ x: 68, y: 72 }),
  callout: Object.freeze({ x: 54, y: 34 }),
  onboarding: Object.freeze({ x: 50, y: 28 }),
  tour: Object.freeze({ x: 54, y: 34 }),
});
const GUIDANCE_STYLE_DEFAULTS = Object.freeze({
  backgroundColor: 'white',
  fontFamily: 'brand',
  fontSize: 'md',
  size: 'md',
  dimensions: Object.freeze({
    width: null,
    height: null,
  }),
  arrow: Object.freeze({
    enabled: false,
    side: 'bottom',
    offset: 50,
    target: Object.freeze({
      x: 50,
      y: 50,
    }),
  }),
  spotlight: Object.freeze({
    enabled: false,
    shape: 'circle',
    x: 50,
    y: 50,
    size: 18,
  }),
});
const GUIDANCE_STYLE_PRESETS = Object.freeze({
  white: Object.freeze({
    label: 'White',
    bg: 'var(--c-white, #ffffff)',
    text: 'var(--ink-900, #0b1f24)',
    muted: 'var(--ink-500, #6a838a)',
    border: 'var(--line-200, #e2eef0)',
    accent: 'var(--brand-accent, #ff9933)',
  }),
  tint: Object.freeze({
    label: 'Brand tint',
    bg: 'var(--brand-tint, #e5f7f5)',
    text: 'var(--ink-900, #0b1f24)',
    muted: 'var(--ink-700, #324a50)',
    border: 'color-mix(in srgb, var(--brand-deep, #033b4b) 18%, #ffffff)',
    accent: 'var(--brand-deep, #033b4b)',
  }),
  navy: Object.freeze({
    label: 'Deep navy',
    bg: 'var(--brand-deep, #033b4b)',
    text: 'var(--c-white, #ffffff)',
    muted: 'rgba(255, 255, 255, 0.78)',
    border: 'rgba(255, 255, 255, 0.16)',
    accent: 'var(--c-accent, #66cccc)',
  }),
  teal: Object.freeze({
    label: 'Teal',
    bg: 'var(--c-accent, #66cccc)',
    text: 'var(--ink-900, #0b1f24)',
    muted: 'var(--ink-700, #324a50)',
    border: 'color-mix(in srgb, var(--c-accent, #66cccc) 62%, #033b4b)',
    accent: 'var(--brand-deep, #033b4b)',
  }),
  grey: Object.freeze({
    label: 'Grey',
    bg: 'var(--c-grey, #96999d)',
    text: 'var(--c-white, #ffffff)',
    muted: 'rgba(255, 255, 255, 0.78)',
    border: 'rgba(255, 255, 255, 0.18)',
    accent: 'var(--c-white, #ffffff)',
  }),
  orange: Object.freeze({
    label: 'Orange',
    bg: 'var(--brand-accent, #ff9933)',
    text: 'var(--ink-900, #0b1f24)',
    muted: 'rgba(11, 31, 36, 0.78)',
    border: 'color-mix(in srgb, var(--brand-accent, #ff9933) 70%, #033b4b)',
    accent: 'var(--brand-deep, #033b4b)',
  }),
});
const GUIDANCE_FONT_FAMILIES = Object.freeze([
  'brand',
  'system',
  'serif',
  'mono',
]);
const GUIDANCE_FONT_SIZES = Object.freeze(['sm', 'md', 'lg', 'xl']);
const GUIDANCE_SURFACE_SIZES = Object.freeze(['sm', 'md', 'lg', 'xl']);
const GUIDANCE_ARROW_SIDES = Object.freeze(['top', 'right', 'bottom', 'left']);
const GUIDANCE_SPOTLIGHT_SHAPES = Object.freeze(['circle']);
const GUIDANCE_DIMENSION_LIMITS = Object.freeze({
  minWidth: 220,
  maxWidth: 900,
  minHeight: 120,
  maxHeight: 720,
});

/**
 * Escape a string for safe HTML interpolation.
 *
 * @param {unknown} value - Arbitrary value.
 * @returns {string} HTML-safe string.
 */
export function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * Return the default viewport placement for a guidance surface.
 *
 * @param {string} placement - Campaign placement.
 * @returns {{ x: number, y: number }} Default top-left viewport percent.
 */
export function getDefaultGuidancePosition(placement = 'banner') {
  return {
    ...(DEFAULT_GUIDANCE_POSITIONS[placement] ||
      DEFAULT_GUIDANCE_POSITIONS.banner),
  };
}

/**
 * Clamp a percentage value to the viewport coordinate range.
 *
 * @param {unknown} value - Candidate percentage.
 * @param {number} fallback - Fallback value when parsing fails.
 * @returns {number} Safe percentage value.
 */
function clampGuidancePercent(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(100, Math.max(0, parsed));
}

/**
 * Clamp a numeric value to an inclusive range.
 *
 * @param {unknown} value - Candidate number.
 * @param {number} fallback - Fallback when parsing fails.
 * @param {number} min - Minimum value.
 * @param {number} max - Maximum value.
 * @returns {number} Clamped number.
 */
function clampGuidanceNumber(value, fallback, min, max) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

/**
 * Pick an allowlisted string value.
 *
 * @param {unknown} value - Candidate value.
 * @param {readonly string[]} allowed - Allowed values.
 * @param {string} fallback - Fallback value.
 * @returns {string} Safe allowlisted value.
 */
function pickGuidanceOption(value, allowed, fallback) {
  const candidate = String(value || '').trim();
  return allowed.includes(candidate) ? candidate : fallback;
}

/**
 * Normalize an optional pixel dimension.
 *
 * @param {unknown} value - Candidate dimension.
 * @param {number|null} fallback - Fallback dimension.
 * @param {number} min - Minimum dimension.
 * @param {number} max - Maximum dimension.
 * @returns {number|null} Clamped pixel dimension, or null for auto.
 */
function normalizeGuidanceDimension(value, fallback, min, max) {
  if (value === null || value === undefined || value === '') {
    return fallback ?? null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback ?? null;
  return Math.round(Math.min(max, Math.max(min, parsed)));
}

/**
 * Normalize optional exact surface dimensions.
 *
 * @param {Record<string, any>|null|undefined} value - Candidate dimensions.
 * @returns {{ width: number|null, height: number|null }} Normalized dimensions.
 */
function normalizeGuidanceDimensions(value = {}) {
  const safeValue = value && typeof value === 'object' ? value : {};
  return {
    width: normalizeGuidanceDimension(
      safeValue.width,
      GUIDANCE_STYLE_DEFAULTS.dimensions.width,
      GUIDANCE_DIMENSION_LIMITS.minWidth,
      GUIDANCE_DIMENSION_LIMITS.maxWidth
    ),
    height: normalizeGuidanceDimension(
      safeValue.height,
      GUIDANCE_STYLE_DEFAULTS.dimensions.height,
      GUIDANCE_DIMENSION_LIMITS.minHeight,
      GUIDANCE_DIMENSION_LIMITS.maxHeight
    ),
  };
}

/**
 * Normalize structured guidance styling metadata.
 *
 * @param {Record<string, any>|null|undefined} value - Candidate style object.
 * @returns {Record<string, any>} Normalized style object.
 */
export function normalizeGuidanceStyle(value = {}) {
  const safeValue = value && typeof value === 'object' ? value : {};
  const safeArrow =
    safeValue.arrow && typeof safeValue.arrow === 'object'
      ? safeValue.arrow
      : {};
  const safeSpotlight =
    safeValue.spotlight && typeof safeValue.spotlight === 'object'
      ? safeValue.spotlight
      : {};
  return {
    backgroundColor: pickGuidanceOption(
      safeValue.backgroundColor,
      Object.keys(GUIDANCE_STYLE_PRESETS),
      GUIDANCE_STYLE_DEFAULTS.backgroundColor
    ),
    fontFamily: pickGuidanceOption(
      safeValue.fontFamily,
      GUIDANCE_FONT_FAMILIES,
      GUIDANCE_STYLE_DEFAULTS.fontFamily
    ),
    fontSize: pickGuidanceOption(
      safeValue.fontSize,
      GUIDANCE_FONT_SIZES,
      GUIDANCE_STYLE_DEFAULTS.fontSize
    ),
    size: pickGuidanceOption(
      safeValue.size,
      GUIDANCE_SURFACE_SIZES,
      GUIDANCE_STYLE_DEFAULTS.size
    ),
    dimensions: normalizeGuidanceDimensions(safeValue.dimensions),
    arrow: {
      enabled: safeArrow.enabled === true,
      side: pickGuidanceOption(
        safeArrow.side,
        GUIDANCE_ARROW_SIDES,
        GUIDANCE_STYLE_DEFAULTS.arrow.side
      ),
      offset: clampGuidancePercent(
        safeArrow.offset,
        GUIDANCE_STYLE_DEFAULTS.arrow.offset
      ),
      target: normalizeGuidancePosition(
        safeArrow.target,
        GUIDANCE_STYLE_DEFAULTS.arrow.target
      ),
    },
    spotlight: {
      enabled: safeSpotlight.enabled === true,
      shape: pickGuidanceOption(
        safeSpotlight.shape,
        GUIDANCE_SPOTLIGHT_SHAPES,
        GUIDANCE_STYLE_DEFAULTS.spotlight.shape
      ),
      x: clampGuidancePercent(
        safeSpotlight.x,
        GUIDANCE_STYLE_DEFAULTS.spotlight.x
      ),
      y: clampGuidancePercent(
        safeSpotlight.y,
        GUIDANCE_STYLE_DEFAULTS.spotlight.y
      ),
      size: clampGuidanceNumber(
        safeSpotlight.size,
        GUIDANCE_STYLE_DEFAULTS.spotlight.size,
        6,
        80
      ),
    },
  };
}

/**
 * Normalize manual guidance position metadata.
 *
 * @param {unknown} value - Candidate position object.
 * @param {{ x: number, y: number }} [fallback=getDefaultGuidancePosition()] - Fallback position.
 * @returns {{ x: number, y: number }} Normalized viewport percent.
 */
export function normalizeGuidancePosition(
  value,
  fallback = getDefaultGuidancePosition()
) {
  const safeValue = value && typeof value === 'object' ? value : {};
  return {
    x: clampGuidancePercent(safeValue.x, fallback.x),
    y: clampGuidancePercent(safeValue.y, fallback.y),
  };
}

/**
 * Return viewport dimensions with jsdom-friendly fallbacks.
 *
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport-like object.
 * @returns {{ width: number, height: number }} Viewport dimensions.
 */
function getGuidanceViewportSize(viewport = window) {
  return {
    width:
      Number(viewport?.innerWidth) ||
      document.documentElement?.clientWidth ||
      1024,
    height:
      Number(viewport?.innerHeight) ||
      document.documentElement?.clientHeight ||
      768,
  };
}

/**
 * Clamp a surface pixel position into the visible viewport.
 *
 * @param {HTMLElement} surface - Guidance surface.
 * @param {number} left - Proposed left coordinate.
 * @param {number} top - Proposed top coordinate.
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport-like object.
 * @returns {{ left: number, top: number }} Clamped pixel position.
 */
function clampGuidancePixels(surface, left, top, viewport = window) {
  const { width: viewportWidth, height: viewportHeight } =
    getGuidanceViewportSize(viewport);
  const rect =
    typeof surface?.getBoundingClientRect === 'function'
      ? surface.getBoundingClientRect()
      : {};
  const surfaceWidth = Math.min(
    Number(rect.width) || surface?.offsetWidth || 352,
    viewportWidth - GUIDANCE_POSITION_MARGIN * 2
  );
  const surfaceHeight = Math.min(
    Number(rect.height) || surface?.offsetHeight || 160,
    viewportHeight - GUIDANCE_POSITION_MARGIN * 2
  );
  const maxLeft = Math.max(
    GUIDANCE_POSITION_MARGIN,
    viewportWidth - surfaceWidth - GUIDANCE_POSITION_MARGIN
  );
  const maxTop = Math.max(
    GUIDANCE_POSITION_MARGIN,
    viewportHeight - surfaceHeight - GUIDANCE_POSITION_MARGIN
  );

  return {
    left: Math.min(
      Math.max(
        Number(left) || GUIDANCE_POSITION_MARGIN,
        GUIDANCE_POSITION_MARGIN
      ),
      maxLeft
    ),
    top: Math.min(
      Math.max(
        Number(top) || GUIDANCE_POSITION_MARGIN,
        GUIDANCE_POSITION_MARGIN
      ),
      maxTop
    ),
  };
}

/**
 * Convert a stored percentage position into clamped pixels.
 *
 * @param {HTMLElement} surface - Guidance surface.
 * @param {{ x: number, y: number }} position - Viewport percent position.
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport-like object.
 * @returns {{ left: number, top: number }} Clamped pixel position.
 */
function pixelsFromGuidancePosition(surface, position, viewport = window) {
  const { width, height } = getGuidanceViewportSize(viewport);
  return clampGuidancePixels(
    surface,
    (position.x / 100) * width,
    (position.y / 100) * height,
    viewport
  );
}

/**
 * Convert a surface pixel position into stored viewport percentages.
 *
 * @param {number} left - Surface left coordinate.
 * @param {number} top - Surface top coordinate.
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport-like object.
 * @returns {{ x: number, y: number }} Stored viewport percent position.
 */
function guidancePositionFromPixels(left, top, viewport = window) {
  const { width, height } = getGuidanceViewportSize(viewport);
  return normalizeGuidancePosition({
    x: Number(((Number(left) / width) * 100).toFixed(2)),
    y: Number(((Number(top) / height) * 100).toFixed(2)),
  });
}

/**
 * Convert a spotlight center percentage into clamped pixels.
 *
 * @param {HTMLElement} spotlight - Spotlight element.
 * @param {Record<string, any>} style - Normalized guidance style.
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport-like object.
 * @returns {{ left: number, top: number, size: number }} Pixel placement.
 */
function pixelsFromGuidanceSpotlight(spotlight, style, viewport = window) {
  const { width, height } = getGuidanceViewportSize(viewport);
  const minDimension = Math.max(1, Math.min(width, height));
  const size = Math.max(
    36,
    Math.round((style.spotlight.size / 100) * minDimension)
  );
  const radius = size / 2;
  const left = (style.spotlight.x / 100) * width - radius;
  const top = (style.spotlight.y / 100) * height - radius;
  return {
    left: Math.min(
      Math.max(left, GUIDANCE_POSITION_MARGIN),
      width - size - GUIDANCE_POSITION_MARGIN
    ),
    top: Math.min(
      Math.max(top, GUIDANCE_POSITION_MARGIN),
      height - size - GUIDANCE_POSITION_MARGIN
    ),
    size,
  };
}

/**
 * Convert a spotlight pixel position into stored center percentages.
 *
 * @param {number} left - Spotlight left coordinate.
 * @param {number} top - Spotlight top coordinate.
 * @param {number} size - Spotlight size in pixels.
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport-like object.
 * @returns {{ x: number, y: number }} Stored center percent.
 */
function guidanceSpotlightPositionFromPixels(
  left,
  top,
  size,
  viewport = window
) {
  const { width, height } = getGuidanceViewportSize(viewport);
  return normalizeGuidancePosition({
    x: Number((((Number(left) + size / 2) / width) * 100).toFixed(2)),
    y: Number((((Number(top) + size / 2) / height) * 100).toFixed(2)),
  });
}

/**
 * Apply saved spotlight placement to a circle overlay.
 *
 * @param {HTMLElement} spotlight - Spotlight overlay.
 * @param {Record<string, any>} style - Normalized guidance style.
 * @returns {void}
 */
function applyGuidanceSpotlightPosition(spotlight, style) {
  if (!spotlight) return;
  spotlight.__guidancePositionCleanup?.();
  const render = () => {
    const next = pixelsFromGuidanceSpotlight(spotlight, style);
    spotlight.style.left = `${next.left}px`;
    spotlight.style.top = `${next.top}px`;
    spotlight.style.width = `${next.size}px`;
    spotlight.style.height = `${next.size}px`;
    spotlight.dataset.guidanceSpotlightX = String(style.spotlight.x);
    spotlight.dataset.guidanceSpotlightY = String(style.spotlight.y);
    spotlight.dataset.guidanceSpotlightSize = String(style.spotlight.size);
  };
  render();
  const scheduleFrame =
    globalThis.requestAnimationFrame ||
    ((callback) => {
      callback();
      return 0;
    });
  scheduleFrame(render);
  window.addEventListener('resize', render);
  spotlight.__guidancePositionCleanup = () => {
    window.removeEventListener('resize', render);
    delete spotlight.__guidancePositionCleanup;
  };
  spotlight.addEventListener(
    'guidance:remove',
    spotlight.__guidancePositionCleanup,
    {
      once: true,
    }
  );
}

/**
 * Apply saved manual placement to a guidance surface.
 *
 * @param {HTMLElement} surface - Guidance surface.
 * @param {unknown} position - Stored position metadata.
 * @param {string} [placement='banner'] - Campaign placement.
 * @returns {{ x: number, y: number }} Normalized position.
 */
function applyGuidancePosition(surface, position, placement = 'banner') {
  const normalized = normalizeGuidancePosition(
    position,
    getDefaultGuidancePosition(placement)
  );
  if (!surface) return normalized;
  surface.__guidancePositionCleanup?.();
  const render = () => {
    const next = pixelsFromGuidancePosition(surface, normalized);
    surface.classList.add('guidance-surface--manual');
    surface.style.position = 'fixed';
    surface.style.left = `${next.left}px`;
    surface.style.top = `${next.top}px`;
    surface.style.right = 'auto';
    surface.style.bottom = 'auto';
    surface.dataset.guidancePositionX = String(normalized.x);
    surface.dataset.guidancePositionY = String(normalized.y);
    surface.dispatchEvent(new Event('guidance:layout'));
  };
  render();
  const scheduleFrame =
    globalThis.requestAnimationFrame ||
    ((callback) => {
      callback();
      return 0;
    });
  scheduleFrame(render);
  window.addEventListener('resize', render);
  surface.__guidancePositionCleanup = () => {
    window.removeEventListener('resize', render);
    delete surface.__guidancePositionCleanup;
  };
  surface.addEventListener(
    'guidance:remove',
    surface.__guidancePositionCleanup,
    {
      once: true,
    }
  );
  return normalized;
}

/**
 * Format manual placement for compact operator display.
 *
 * @param {unknown} position - Candidate position.
 * @returns {string} Human-readable placement copy.
 */
function formatGuidancePosition(position) {
  const normalized = normalizeGuidancePosition(position);
  return `${Math.round(normalized.x)}% across, ${Math.round(normalized.y)}% down`;
}

/**
 * Enable pointer dragging for a preview guidance surface.
 *
 * @param {HTMLElement} surface - Preview surface to drag.
 * @param {(position: { x: number, y: number }) => void} onPositionChange - Placement callback.
 * @returns {void}
 */
function enableGuidanceSurfaceDragging(surface, onPositionChange) {
  if (!surface) return;
  surface.dataset.guidanceDraggable = 'true';

  let dragState = null;
  function stopDrag() {
    dragState = null;
    surface.classList.remove('guidance-builder-preview--dragging');
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', stopDrag);
    document.removeEventListener('pointercancel', stopDrag);
  }
  function onPointerMove(event) {
    if (!dragState) return;
    const next = clampGuidancePixels(
      surface,
      event.clientX - dragState.offsetX,
      event.clientY - dragState.offsetY
    );
    surface.style.left = `${next.left}px`;
    surface.style.top = `${next.top}px`;
    surface.style.right = 'auto';
    surface.style.bottom = 'auto';
    onPositionChange(guidancePositionFromPixels(next.left, next.top));
    surface.dispatchEvent(new Event('guidance:layout'));
  }

  surface.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 && event.pointerType !== 'touch') return;
    if (
      event.target.closest(
        'a, button, input, label, select, summary, textarea, [data-guidance-resize-handle], [data-guidance-arrow-handle]'
      )
    ) {
      return;
    }

    const rect = surface.getBoundingClientRect();
    dragState = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    surface.setPointerCapture?.(event.pointerId);
    surface.classList.add('guidance-builder-preview--dragging');
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', stopDrag);
    document.addEventListener('pointercancel', stopDrag);
    event.preventDefault();
  });
}

/**
 * Clamp exact surface dimensions to responsive authoring bounds.
 *
 * @param {unknown} width - Candidate width.
 * @param {unknown} height - Candidate height.
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport-like object.
 * @returns {{ width: number, height: number }} Clamped dimensions.
 */
function clampGuidanceDimensions(width, height, viewport = window) {
  const { width: viewportWidth, height: viewportHeight } =
    getGuidanceViewportSize(viewport);
  return {
    width: Math.round(
      clampGuidanceNumber(
        width,
        416,
        GUIDANCE_DIMENSION_LIMITS.minWidth,
        Math.min(
          GUIDANCE_DIMENSION_LIMITS.maxWidth,
          viewportWidth - GUIDANCE_POSITION_MARGIN * 2
        )
      )
    ),
    height: Math.round(
      clampGuidanceNumber(
        height,
        180,
        GUIDANCE_DIMENSION_LIMITS.minHeight,
        Math.min(
          GUIDANCE_DIMENSION_LIMITS.maxHeight,
          viewportHeight - GUIDANCE_POSITION_MARGIN * 2
        )
      )
    ),
  };
}

/**
 * Enable pointer resizing for a builder preview guidance surface.
 *
 * @param {HTMLElement} surface - Preview surface to resize.
 * @param {(dimensions: { width: number, height: number }) => void} onDimensionsChange - Resize callback.
 * @returns {void}
 */
function enableGuidanceSurfaceResizing(surface, onDimensionsChange) {
  if (!surface || surface.dataset.guidanceResizeBound === 'true') return;
  surface.dataset.guidanceResizeBound = 'true';
  const handle = document.createElement('button');
  handle.type = 'button';
  handle.className = 'guidance-resize-handle';
  handle.dataset.guidanceResizeHandle = 'true';
  handle.setAttribute('aria-label', 'Resize guidance');
  surface.appendChild(handle);

  let resizeState = null;
  function stopResize() {
    resizeState = null;
    surface.classList.remove('guidance-builder-preview--resizing');
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', stopResize);
    document.removeEventListener('pointercancel', stopResize);
  }
  function onPointerMove(event) {
    if (!resizeState) return;
    const next = clampGuidanceDimensions(
      resizeState.startWidth + (event.clientX - resizeState.startX),
      resizeState.startHeight + (event.clientY - resizeState.startY)
    );
    surface.style.setProperty('--guidance-width', `${next.width}px`);
    surface.style.setProperty('--guidance-height', `${next.height}px`);
    surface.dataset.guidanceCustomWidth = 'true';
    surface.dataset.guidanceCustomHeight = 'true';
    onDimensionsChange(next);
    surface.dispatchEvent(new Event('guidance:layout'));
  }

  handle.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 && event.pointerType !== 'touch') return;
    const rect = surface.getBoundingClientRect();
    resizeState = {
      startX: event.clientX,
      startY: event.clientY,
      startWidth: Number(rect.width) || surface.offsetWidth || 416,
      startHeight: Number(rect.height) || surface.offsetHeight || 180,
    };
    handle.setPointerCapture?.(event.pointerId);
    surface.classList.add('guidance-builder-preview--resizing');
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', stopResize);
    document.addEventListener('pointercancel', stopResize);
    event.preventDefault();
  });
}

/**
 * Enable pointer dragging for a preview spotlight circle.
 *
 * @param {HTMLElement} spotlight - Preview spotlight to drag.
 * @param {(position: { x: number, y: number }) => void} onPositionChange - Placement callback.
 * @returns {void}
 */
function enableGuidanceSpotlightDragging(spotlight, onPositionChange) {
  if (!spotlight) return;
  spotlight.dataset.guidanceDraggable = 'true';

  let dragState = null;
  function stopDrag() {
    dragState = null;
    spotlight.classList.remove('guidance-builder-preview--dragging');
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', stopDrag);
    document.removeEventListener('pointercancel', stopDrag);
  }
  function onPointerMove(event) {
    if (!dragState) return;
    const { width, height } = getGuidanceViewportSize();
    const size = dragState.size;
    const nextLeft = Math.min(
      Math.max(event.clientX - dragState.offsetX, GUIDANCE_POSITION_MARGIN),
      width - size - GUIDANCE_POSITION_MARGIN
    );
    const nextTop = Math.min(
      Math.max(event.clientY - dragState.offsetY, GUIDANCE_POSITION_MARGIN),
      height - size - GUIDANCE_POSITION_MARGIN
    );
    spotlight.style.left = `${nextLeft}px`;
    spotlight.style.top = `${nextTop}px`;
    onPositionChange(
      guidanceSpotlightPositionFromPixels(nextLeft, nextTop, size)
    );
  }

  spotlight.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 && event.pointerType !== 'touch') return;
    const rect = spotlight.getBoundingClientRect();
    dragState = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      size: Number(rect.width) || spotlight.offsetWidth || 120,
    };
    spotlight.setPointerCapture?.(event.pointerId);
    spotlight.classList.add('guidance-builder-preview--dragging');
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', stopDrag);
    document.addEventListener('pointercancel', stopDrag);
    event.preventDefault();
  });
}

/**
 * Determine whether runtime guidance is allowed on a path.
 *
 * @param {string} pathname - Current browser pathname.
 * @param {{ builderMode?: boolean }} [options={}] - Runtime options.
 * @returns {boolean} True when guidance may run.
 */
export function shouldRunGuidanceOnPath(pathname, options = {}) {
  const path = String(pathname || '/');
  const isAuthPath =
    path === '/' ||
    path.endsWith('/index.html') ||
    path.endsWith('/forgot-password.html') ||
    path.endsWith('/reset-password.html');
  if (isAuthPath) return false;
  return options.builderMode === true || !path.endsWith('/focusmode.html');
}

/**
 * Resolve the display mode for one campaign from its placement.
 *
 * @param {Record<string, any>} campaign - Candidate guidance campaign.
 * @returns {'announcement_center'|'banner'|'callout'|'tour'|'onboarding'} Display mode.
 */
export function getCampaignDisplayMode(campaign) {
  const placement = String(campaign?.placement || '').trim();
  if (placement === 'tour') return 'tour';
  if (placement === 'callout') return 'callout';
  if (placement === 'banner') return 'banner';
  if (campaign?.type === 'onboarding') return 'onboarding';
  return 'announcement_center';
}

/**
 * Return the ordered steps for a light tour campaign.
 *
 * @param {Record<string, any>} campaign - Guidance campaign.
 * @returns {Array<{ title: string, body: string, position: { x: number, y: number } }>} Tour steps.
 */
export function getTourSteps(campaign) {
  const campaignPosition = normalizeGuidancePosition(
    campaign?.position,
    getDefaultGuidancePosition('tour')
  );
  const storedSteps = Array.isArray(campaign?.steps) ? campaign.steps : [];
  const steps = storedSteps
    .map((step) => ({
      title: String(step?.title || '').trim(),
      body: String(step?.body || '').trim(),
      position: normalizeGuidancePosition(step?.position, campaignPosition),
    }))
    .filter((step) => step.title && step.body);
  if (steps.length) return steps;
  const fallback = {
    title: String(campaign?.title || '').trim(),
    body: String(campaign?.body || '').trim(),
    position: campaignPosition,
  };
  return fallback.title && fallback.body ? [fallback] : [];
}

/**
 * Return non-announcement campaigns in the order they should interrupt.
 *
 * @param {Array<Record<string, any>>} campaigns - Eligible campaigns.
 * @returns {Array<Record<string, any>>} Automatic campaigns.
 */
export function getAutomaticCampaigns(campaigns) {
  const rankCampaign = (campaign) => {
    if (campaign?.type === 'onboarding') return 0;
    if (['callout', 'tour'].includes(campaign?.placement)) return 1;
    return 2;
  };
  return (Array.isArray(campaigns) ? campaigns : [])
    .filter((campaign) => campaign?.placement !== 'announcement_center')
    .map((campaign, index) => ({
      campaign,
      index,
      rank: rankCampaign(campaign),
    }))
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map(({ campaign }) => campaign);
}

/**
 * Choose the first automatic campaign to display.
 *
 * @param {Array<Record<string, any>>} campaigns - Eligible campaigns.
 * @returns {{ campaign: Record<string, any>, mode: string }|null} Selected campaign and display mode.
 */
export function selectAutomaticCampaign(campaigns) {
  const candidates = getAutomaticCampaigns(campaigns);
  const onboarding = candidates.find(
    (campaign) => campaign.type === 'onboarding'
  );
  if (onboarding) {
    return {
      campaign: onboarding,
      mode: getCampaignDisplayMode(onboarding),
    };
  }

  const callout = candidates.find((campaign) =>
    ['callout', 'tour'].includes(campaign.placement)
  );
  if (callout) {
    return { campaign: callout, mode: getCampaignDisplayMode(callout) };
  }

  const banner = candidates.find((campaign) => campaign.placement === 'banner');
  return banner ? { campaign: banner, mode: 'banner' } : null;
}

/**
 * Return the campaigns that belong in the announcement center.
 *
 * @param {Array<Record<string, any>>} campaigns - Eligible campaigns.
 * @returns {Array<Record<string, any>>} Announcement-center campaigns.
 */
export function getAnnouncementCenterCampaigns(campaigns) {
  return (Array.isArray(campaigns) ? campaigns : []).filter(
    (campaign) => campaign.placement === 'announcement_center'
  );
}

/**
 * Determine whether the current URL asks for in-page guidance authoring.
 *
 * @param {string} search - URL search string.
 * @returns {boolean} True when builder mode is requested.
 */
export function isGuidanceBuilderMode(search = '') {
  const params = new URLSearchParams(String(search || ''));
  return ['1', 'true', 'yes'].includes(
    String(params.get('guidanceBuilder') || '').toLowerCase()
  );
}

/**
 * Split free-form admin input into a normalized unique list.
 *
 * @param {unknown} value - Candidate array or comma-separated string.
 * @returns {string[]} Trimmed non-empty unique entries.
 */
function parseBuilderList(value) {
  const entries = Array.isArray(value)
    ? value
    : String(value || '').split(/\n|,/);
  return [
    ...new Set(entries.map((entry) => String(entry).trim()).filter(Boolean)),
  ];
}

/**
 * Convert a datetime-local value to an ISO timestamp.
 *
 * @param {unknown} value - Candidate local date/time value.
 * @returns {string|null} ISO timestamp or null.
 */
function localBuilderDateTimeToIso(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/**
 * Normalize builder tour steps and append the current fields as the final step.
 *
 * @param {Record<string, any>} values - Builder field values.
 * @returns {Array<{ title: string, body: string, position: { x: number, y: number } }>} Ordered steps.
 */
function buildBuilderTourSteps(values = {}) {
  const currentPosition = normalizeGuidancePosition(
    values.position,
    getDefaultGuidancePosition('tour')
  );
  const steps = Array.isArray(values.steps)
    ? values.steps
        .map((step) => ({
          title: String(step?.title || '').trim(),
          body: String(step?.body || '').trim(),
          position: normalizeGuidancePosition(step?.position, currentPosition),
        }))
        .filter((step) => step.title || step.body)
    : [];
  const currentStep = {
    title: String(values.title || '').trim(),
    body: String(values.body || '').trim(),
    position: currentPosition,
  };
  const currentStepIsComplete = currentStep.title && currentStep.body;
  const alreadyAdded = steps.some(
    (step) =>
      step.title === currentStep.title &&
      step.body === currentStep.body &&
      step.position.x === currentStep.position.x &&
      step.position.y === currentStep.position.y
  );
  if (currentStepIsComplete && !alreadyAdded) {
    steps.push(currentStep);
  }
  return steps;
}

/**
 * Build a guidance campaign payload from the in-page builder state.
 *
 * @param {Record<string, any>} values - Builder field values.
 * @param {string} pathname - Current page path.
 * @returns {Record<string, any>} Draft campaign payload.
 */
export function buildBuilderCampaignPayload(values = {}, pathname = '/') {
  const tenantIds = parseBuilderList(values.tenantIds);
  const roles = parseBuilderList(values.roles);
  const steps =
    values.placement === 'tour' ? buildBuilderTourSteps(values) : [];
  const ctaLabel = String(values.ctaLabel || '').trim();
  const ctaUrl = String(values.ctaUrl || '').trim();
  const path = String(pathname || '/').trim() || '/';
  const startsAt = localBuilderDateTimeToIso(values.startsAt);
  const endsAt = localBuilderDateTimeToIso(values.endsAt);
  const placement = values.placement || 'banner';
  const position = normalizeGuidancePosition(
    values.position,
    getDefaultGuidancePosition(placement)
  );
  return {
    ...(values.campaignId ? { campaignId: values.campaignId } : {}),
    type: values.type || 'announcement',
    title: String(values.title || '').trim(),
    body: String(values.body || '').trim(),
    cta:
      ctaLabel || ctaUrl
        ? {
            label: ctaLabel,
            url: ctaUrl,
          }
        : null,
    audience: {
      allTenants: tenantIds.length === 0,
      tenantIds,
      roles: roles.length ? roles : ['all'],
    },
    placement,
    pageMatchers: [path],
    position,
    style: normalizeGuidanceStyle(values.style),
    steps: placement === 'tour' ? steps : [],
    startsAt,
    endsAt,
    priority: Number(values.priority || 0),
    reminderCooldownHours: Number(values.reminderCooldownHours || 24),
  };
}

/**
 * Validate a builder payload before it is submitted.
 *
 * @param {Record<string, any>} payload - Campaign payload.
 * @returns {string} Validation error, or an empty string when valid.
 */
export function validateBuilderCampaignPayload(payload) {
  if (!payload.title) return 'Add a title before saving.';
  if (!payload.body) return 'Add body copy before saving.';

  const hasTourSteps =
    payload.placement === 'tour' &&
    Array.isArray(payload.steps) &&
    payload.steps.length > 0;
  if (hasTourSteps) {
    for (const [index, step] of payload.steps.entries()) {
      const title = String(step?.title || '').trim();
      const body = String(step?.body || '').trim();
      const label = `Step ${index + 1}`;
      if (!title) return `${label} needs a title.`;
      if (!body) return `${label} needs body copy.`;
    }
  }
  if (payload.cta && (!payload.cta.label || !payload.cta.url)) {
    return 'CTA label and URL must both be set.';
  }
  if (
    payload.startsAt &&
    payload.endsAt &&
    Date.parse(payload.startsAt) >= Date.parse(payload.endsAt)
  ) {
    return 'End date must be after start date.';
  }
  return '';
}

/**
 * Record campaign progress through the backend.
 *
 * @param {object} di - Topsail dependency container.
 * @param {string} campaignId - Campaign id.
 * @param {'view'|'dismiss'|'skip'|'complete'} action - Progress action.
 * @returns {Promise<void>} Resolves when recorded or skipped.
 */
async function recordProgress(di, campaignId, action) {
  if (!campaignId || !di?.apiClient || !di?.endpoints) return;
  await di.apiClient.fetchJson(di.endpoints.recordGuidanceCampaignProgress(), {
    auth: true,
    body: { campaignId, action },
  });
}

/**
 * Log optional campaign-progress failures without promoting fetch outages to warnings.
 *
 * @param {string} message - Console message prefix.
 * @param {unknown} error - Progress recording error.
 * @returns {void}
 */
function logGuidanceProgressFailure(message, error) {
  if (isGuidanceFetchUnavailable(error)) {
    console.debug(message, {
      message: error?.message || String(error),
    });
    return;
  }
  console.warn(message, error);
}

/**
 * Create a CTA button or link for a campaign.
 *
 * @param {Record<string, any>} campaign - Guidance campaign.
 * @returns {string} CTA markup.
 */
function buildCtaMarkup(campaign) {
  if (!campaign?.cta?.label || !campaign?.cta?.url) return '';
  return `<a class="btn btn--secondary" href="${escapeHtml(campaign.cta.url)}">${escapeHtml(campaign.cta.label)}</a>`;
}

/**
 * Build completion controls for a guidance surface.
 *
 * @param {Record<string, any>} campaign - Guidance campaign.
 * @param {{ primaryLabel?: string }} [options={}] - Display options.
 * @returns {string} Action button markup.
 */
function buildCompletionActions(campaign, options = {}) {
  if (campaign?.type !== 'onboarding') {
    return `<button type="button" class="btn btn--secondary" data-guidance-action="complete">${escapeHtml(options.primaryLabel || 'Got it')}</button>`;
  }
  return `
    <button type="button" class="btn btn--secondary" data-guidance-action="skip">Skip for now</button>
    <button type="button" class="btn btn--primary" data-guidance-action="complete">${escapeHtml(options.primaryLabel || 'Done')}</button>
  `;
}

/**
 * Render stored guidance body copy with paragraph and line breaks preserved.
 *
 * @param {unknown} value - Stored body copy.
 * @returns {string} Safe paragraph markup.
 */
function buildGuidanceBodyMarkup(value) {
  const paragraphs = String(value || '')
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  if (!paragraphs.length) return '<p></p>';
  return paragraphs
    .map((paragraph) => {
      const lines = paragraph
        .split('\n')
        .map((line) => escapeHtml(line.trimEnd()));
      return `<p>${lines.join('<br>')}</p>`;
    })
    .join('');
}

/**
 * Build shared modal-like guidance surface markup.
 *
 * @param {Record<string, any>} campaign - Guidance campaign.
 * @param {{ titleId?: string, kicker?: string, body?: string, actions?: string }} [options={}] - Markup options.
 * @returns {string} Guidance surface markup.
 */
function buildGuidanceSurfaceMarkup(campaign, options = {}) {
  const titleId = options.titleId ? ` id="${escapeHtml(options.titleId)}"` : '';
  const kicker = options.kicker
    ? `<p class="guidance-surface__eyebrow">${escapeHtml(options.kicker)}</p>`
    : '';
  const actions = String(options.actions || '').trim();
  return `
    <header class="guidance-surface__header">
      <div>
        ${kicker}
        <h2${titleId}>${escapeHtml(campaign.title)}</h2>
      </div>
    </header>
    <div class="guidance-surface__body">
      ${buildGuidanceBodyMarkup(options.body || campaign.body)}
    </div>
    ${
      actions
        ? `<footer class="guidance-actions guidance-surface__footer">${actions}</footer>`
        : ''
    }
  `;
}

/**
 * Apply normalized visual styling to a guidance surface.
 *
 * @param {HTMLElement} surface - Guidance card-like surface.
 * @param {Record<string, any>|null|undefined} styleValue - Campaign style metadata.
 * @returns {Record<string, any>} Normalized style.
 */
function applyGuidanceStyle(surface, styleValue) {
  const style = normalizeGuidanceStyle(styleValue);
  const preset =
    GUIDANCE_STYLE_PRESETS[style.backgroundColor] ||
    GUIDANCE_STYLE_PRESETS[GUIDANCE_STYLE_DEFAULTS.backgroundColor];
  if (!surface) return style;

  surface.__guidanceStyle = style;
  surface.classList.add('guidance-surface');
  surface.dataset.guidanceBackground = style.backgroundColor;
  surface.dataset.guidanceFontFamily = style.fontFamily;
  surface.dataset.guidanceFontSize = style.fontSize;
  surface.dataset.guidanceSize = style.size;
  surface.dataset.guidanceCustomWidth = style.dimensions.width ? 'true' : '';
  surface.dataset.guidanceCustomHeight = style.dimensions.height ? 'true' : '';
  surface.style.setProperty('--guidance-bg', preset.bg);
  surface.style.setProperty('--guidance-text', preset.text);
  surface.style.setProperty('--guidance-muted', preset.muted);
  surface.style.setProperty('--guidance-border', preset.border);
  surface.style.setProperty('--guidance-accent', preset.accent);
  if (style.dimensions.width) {
    surface.style.setProperty(
      '--guidance-width',
      `${style.dimensions.width}px`
    );
  } else {
    surface.style.removeProperty('--guidance-width');
  }
  if (style.dimensions.height) {
    surface.style.setProperty(
      '--guidance-height',
      `${style.dimensions.height}px`
    );
  } else {
    surface.style.removeProperty('--guidance-height');
  }
  return style;
}

/**
 * Remove a spotlight owned by a guidance surface.
 *
 * @param {HTMLElement|null|undefined} owner - Guidance surface or backdrop.
 * @returns {void}
 */
function clearGuidanceOwnerSpotlight(owner) {
  if (!owner?.__guidanceSpotlight) return;
  removeGuidanceSurface(owner.__guidanceSpotlight);
  delete owner.__guidanceSpotlight;
}

/**
 * Render a saved spotlight circle for a styled guidance surface.
 *
 * @param {HTMLElement} owner - Owner surface that controls spotlight lifecycle.
 * @param {Record<string, any>} style - Normalized style metadata.
 * @param {{ draggable?: boolean, onPositionChange?: Function }} [options={}] - Preview behavior.
 * @returns {HTMLElement|null} Rendered spotlight or null.
 */
function renderGuidanceSpotlight(owner, style, options = {}) {
  clearGuidanceOwnerSpotlight(owner);
  if (!style?.spotlight?.enabled) return null;
  const spotlight = document.createElement('div');
  spotlight.className = 'guidance-spotlight';
  spotlight.dataset.guidanceSurface = 'spotlight';
  spotlight.dataset.guidanceSpotlightShape = style.spotlight.shape;
  spotlight.setAttribute('aria-hidden', 'true');
  const host =
    owner?.classList?.contains('guidance-modal-backdrop') ||
    owner?.classList?.contains('guidance-tour-backdrop')
      ? owner
      : document.body;
  host.appendChild(spotlight);
  applyGuidanceSpotlightPosition(spotlight, style);
  if (owner) owner.__guidanceSpotlight = spotlight;
  owner?.addEventListener(
    'guidance:remove',
    () => {
      removeGuidanceSurface(spotlight);
      if (owner.__guidanceSpotlight === spotlight) {
        delete owner.__guidanceSpotlight;
      }
    },
    { once: true }
  );
  if (options.draggable) {
    spotlight.classList.add('guidance-builder-preview');
    enableGuidanceSpotlightDragging(spotlight, (position) => {
      if (typeof options.onPositionChange === 'function') {
        options.onPositionChange(position);
      }
    });
  }
  return spotlight;
}

/**
 * Remove a draggable arrow owned by a guidance surface.
 *
 * @param {HTMLElement|null|undefined} owner - Guidance surface or backdrop.
 * @returns {void}
 */
function clearGuidanceOwnerArrow(owner) {
  if (!owner?.__guidanceArrow) return;
  const { line, handle, cleanup } = owner.__guidanceArrow;
  if (typeof cleanup === 'function') cleanup();
  line?.remove();
  handle?.remove();
  delete owner.__guidanceArrow;
}

/**
 * Convert an arrow target percentage into viewport pixels.
 *
 * @param {{ x: number, y: number }} target - Stored arrow target.
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport-like object.
 * @returns {{ left: number, top: number }} Pixel target.
 */
function pixelsFromGuidanceArrowTarget(target, viewport = window) {
  const { width, height } = getGuidanceViewportSize(viewport);
  const normalized = normalizeGuidancePosition(
    target,
    GUIDANCE_STYLE_DEFAULTS.arrow.target
  );
  return {
    left: (normalized.x / 100) * width,
    top: (normalized.y / 100) * height,
  };
}

/**
 * Convert arrow target pixels into stored viewport percentages.
 *
 * @param {number} left - Target left coordinate.
 * @param {number} top - Target top coordinate.
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport-like object.
 * @returns {{ x: number, y: number }} Stored target percentage.
 */
function guidanceArrowTargetFromPixels(left, top, viewport = window) {
  const { width, height } = getGuidanceViewportSize(viewport);
  return normalizeGuidancePosition({
    x: Number(((Number(left) / width) * 100).toFixed(2)),
    y: Number(((Number(top) / height) * 100).toFixed(2)),
  });
}

/**
 * Return the surface-edge point nearest to an arrow target.
 *
 * @param {DOMRect|Record<string, number>} rect - Surface rectangle.
 * @param {{ left: number, top: number }} target - Arrow target pixels.
 * @returns {{ left: number, top: number }} Arrow line start point.
 */
function getGuidanceArrowStart(rect, target) {
  const centerX = Number(rect.left) + Number(rect.width || 0) / 2;
  const centerY = Number(rect.top) + Number(rect.height || 0) / 2;
  const halfWidth = Math.max(1, Number(rect.width || 0) / 2);
  const halfHeight = Math.max(1, Number(rect.height || 0) / 2);
  const dx = target.left - centerX;
  const dy = target.top - centerY;
  if (!dx && !dy) return { left: centerX, top: centerY };

  const widthScale =
    dx === 0 ? Number.POSITIVE_INFINITY : halfWidth / Math.abs(dx);
  const heightScale =
    dy === 0 ? Number.POSITIVE_INFINITY : halfHeight / Math.abs(dy);
  const scale = Math.min(widthScale, heightScale, 1);
  return {
    left: centerX + dx * scale,
    top: centerY + dy * scale,
  };
}

/**
 * Enable dragging for a builder arrow endpoint.
 *
 * @param {HTMLElement} handle - Arrow endpoint handle.
 * @param {(target: { x: number, y: number }) => void} onTargetChange - Target callback.
 * @param {Function} render - Render callback.
 * @returns {void}
 */
function enableGuidanceArrowDragging(handle, onTargetChange, render) {
  if (!handle) return;
  handle.dataset.guidanceDraggable = 'true';

  let dragState = null;
  function stopDrag() {
    dragState = null;
    handle.classList.remove('guidance-builder-preview--dragging');
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', stopDrag);
    document.removeEventListener('pointercancel', stopDrag);
  }
  function onPointerMove(event) {
    if (!dragState) return;
    const { width, height } = getGuidanceViewportSize();
    const nextLeft = Math.min(
      Math.max(event.clientX, GUIDANCE_POSITION_MARGIN),
      width - GUIDANCE_POSITION_MARGIN
    );
    const nextTop = Math.min(
      Math.max(event.clientY, GUIDANCE_POSITION_MARGIN),
      height - GUIDANCE_POSITION_MARGIN
    );
    onTargetChange(guidanceArrowTargetFromPixels(nextLeft, nextTop));
    render();
  }

  handle.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 && event.pointerType !== 'touch') return;
    dragState = true;
    handle.setPointerCapture?.(event.pointerId);
    handle.classList.add('guidance-builder-preview--dragging');
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', stopDrag);
    document.addEventListener('pointercancel', stopDrag);
    event.preventDefault();
  });
}

/**
 * Render an arrow line from a guidance surface to a saved page coordinate.
 *
 * @param {HTMLElement} owner - Owner surface that controls arrow lifecycle.
 * @param {HTMLElement} surface - Guidance surface the arrow starts from.
 * @param {Record<string, any>} style - Normalized style metadata.
 * @param {{ draggable?: boolean, onTargetChange?: Function }} [options={}] - Builder behavior.
 * @returns {{ line: HTMLElement, handle: HTMLElement|null }|null} Rendered arrow elements.
 */
function renderGuidanceArrow(owner, surface, style, options = {}) {
  clearGuidanceOwnerArrow(owner);
  if (!style?.arrow?.enabled || !surface) return null;

  const line = document.createElement('div');
  line.className = 'guidance-arrow-line';
  line.dataset.guidanceSurface = 'arrow';
  line.setAttribute('aria-hidden', 'true');

  const handle = options.draggable ? document.createElement('button') : null;
  if (handle) {
    handle.type = 'button';
    handle.className = 'guidance-arrow-target guidance-builder-preview';
    handle.dataset.guidanceSurface = 'arrow-target';
    handle.dataset.guidanceArrowHandle = 'true';
    handle.setAttribute('aria-label', 'Move arrow target');
  }

  const host =
    owner?.classList?.contains('guidance-modal-backdrop') ||
    owner?.classList?.contains('guidance-tour-backdrop')
      ? owner
      : document.body;
  host.appendChild(line);
  if (handle) host.appendChild(handle);

  const render = () => {
    const activeStyle = normalizeGuidanceStyle(
      surface.__guidanceStyle || style
    );
    const target = pixelsFromGuidanceArrowTarget(activeStyle.arrow.target);
    const rect =
      typeof surface.getBoundingClientRect === 'function'
        ? surface.getBoundingClientRect()
        : { left: 0, top: 0, width: 0, height: 0 };
    const start = getGuidanceArrowStart(rect, target);
    const dx = target.left - start.left;
    const dy = target.top - start.top;
    const length = Math.max(0, Math.hypot(dx, dy));
    const angle = Math.atan2(dy, dx);

    line.style.left = `${start.left}px`;
    line.style.top = `${start.top}px`;
    line.style.width = `${length}px`;
    line.style.transform = `rotate(${angle}rad)`;
    line.dataset.guidanceArrowTargetX = String(activeStyle.arrow.target.x);
    line.dataset.guidanceArrowTargetY = String(activeStyle.arrow.target.y);

    if (handle) {
      handle.style.left = `${target.left}px`;
      handle.style.top = `${target.top}px`;
      handle.dataset.guidanceArrowTargetX = String(activeStyle.arrow.target.x);
      handle.dataset.guidanceArrowTargetY = String(activeStyle.arrow.target.y);
    }
  };

  render();
  const scheduleFrame =
    globalThis.requestAnimationFrame ||
    ((callback) => {
      callback();
      return 0;
    });
  scheduleFrame(render);
  window.addEventListener('resize', render);
  surface.addEventListener('guidance:layout', render);
  if (handle) {
    enableGuidanceArrowDragging(
      handle,
      (target) => {
        const current = normalizeGuidanceStyle(
          surface.__guidanceStyle || style
        );
        const nextStyle = normalizeGuidanceStyle({
          ...current,
          arrow: {
            ...current.arrow,
            enabled: true,
            target,
          },
        });
        surface.__guidanceStyle = nextStyle;
        if (typeof options.onTargetChange === 'function') {
          options.onTargetChange(target);
        }
      },
      render
    );
  }

  const cleanup = () => {
    window.removeEventListener('resize', render);
    surface.removeEventListener('guidance:layout', render);
  };
  owner.__guidanceArrow = { line, handle, cleanup };
  owner.addEventListener(
    'guidance:remove',
    () => {
      clearGuidanceOwnerArrow(owner);
    },
    { once: true }
  );
  return { line, handle };
}

/**
 * Apply campaign style and optional visual aids to a rendered surface.
 *
 * @param {HTMLElement} surface - Styled guidance surface.
 * @param {Record<string, any>} campaign - Campaign payload.
 * @param {{ owner?: HTMLElement, spotlight?: boolean, spotlightDraggable?: boolean, onSpotlightPositionChange?: Function }} [options={}] - Treatment options.
 * @returns {Record<string, any>} Normalized style metadata.
 */
function applyGuidanceTreatment(surface, campaign, options = {}) {
  const style = applyGuidanceStyle(surface, campaign?.style);
  if (options.spotlight !== false) {
    renderGuidanceSpotlight(options.owner || surface, style, {
      draggable: options.spotlightDraggable === true,
      onPositionChange: options.onSpotlightPositionChange,
    });
  } else {
    clearGuidanceOwnerSpotlight(options.owner || surface);
  }
  return style;
}

/**
 * Remove one guidance surface and notify lifecycle listeners.
 *
 * @param {HTMLElement} surface - Rendered guidance surface.
 * @returns {void}
 */
function removeGuidanceSurface(surface) {
  surface.dispatchEvent(new Event('guidance:remove'));
  surface.remove();
}

/**
 * Remove an existing rendered guidance surface.
 *
 * @returns {void}
 */
function clearCurrentSurface(options = {}) {
  const includeAnnouncementCenter = options.includeAnnouncementCenter !== false;
  document.querySelectorAll('[data-guidance-surface]').forEach((el) => {
    if (
      !includeAnnouncementCenter &&
      ['announcements', 'announcement-list'].includes(
        el.dataset.guidanceSurface
      )
    ) {
      return;
    }
    removeGuidanceSurface(el);
  });
}

/**
 * Wire click actions for a rendered guidance surface.
 *
 * @param {HTMLElement} surface - Rendered guidance surface.
 * @param {object} di - Topsail dependency container.
 * @param {Record<string, any>} campaign - Guidance campaign.
 * @returns {void}
 */
function bindSurfaceActions(surface, di, campaign, options = {}) {
  surface.addEventListener('click', (event) => {
    const button = event.target.closest('[data-guidance-action]');
    if (!button) return;
    const action = button.dataset.guidanceAction;
    void recordProgress(di, campaign.id, action).catch((error) => {
      logGuidanceProgressFailure(
        '[guidanceClient] failed to record progress',
        error
      );
    });
    removeGuidanceSurface(surface);
    if (typeof options.onAfterAction === 'function') {
      void options.onAfterAction({ action, campaign });
    }
  });
}

/**
 * Render a low-interruption banner campaign.
 *
 * @param {object} di - Topsail dependency container.
 * @param {Record<string, any>} campaign - Guidance campaign.
 * @returns {HTMLElement} Rendered banner.
 */
function renderBanner(di, campaign, options = {}) {
  clearCurrentSurface({ includeAnnouncementCenter: false });
  const banner = document.createElement('section');
  banner.className = 'guidance-banner';
  banner.dataset.guidanceSurface = 'banner';
  banner.setAttribute('role', 'status');
  banner.setAttribute('aria-live', 'polite');
  banner.innerHTML = buildGuidanceSurfaceMarkup(campaign, {
    actions: `
      ${buildCtaMarkup(campaign)}
      <button type="button" class="btn btn--secondary" data-guidance-action="${campaign.type === 'onboarding' ? 'skip' : 'dismiss'}">${campaign.type === 'onboarding' ? 'Skip for now' : 'Dismiss'}</button>
    `,
  });
  bindSurfaceActions(banner, di, campaign, options);
  document.body.appendChild(banner);
  applyGuidanceTreatment(banner, campaign);
  applyGuidancePosition(banner, campaign.position, 'banner');
  renderGuidanceArrow(banner, banner, normalizeGuidanceStyle(campaign.style));
  return banner;
}

/**
 * Render a contextual callout campaign.
 *
 * @param {object} di - Topsail dependency container.
 * @param {Record<string, any>} campaign - Guidance campaign.
 * @returns {HTMLElement} Rendered callout.
 */
function renderCallout(di, campaign, options = {}) {
  clearCurrentSurface({ includeAnnouncementCenter: false });
  const callout = document.createElement('section');
  callout.className = 'guidance-callout';
  callout.dataset.guidanceSurface = 'callout';
  callout.setAttribute('role', 'status');
  callout.setAttribute('aria-live', 'polite');
  callout.innerHTML = buildGuidanceSurfaceMarkup(campaign, {
    actions: `
      ${buildCtaMarkup(campaign)}
      ${buildCompletionActions(campaign, { primaryLabel: 'Got it' })}
    `,
  });
  bindSurfaceActions(callout, di, campaign, options);
  document.body.appendChild(callout);
  applyGuidanceTreatment(callout, campaign);
  applyGuidancePosition(callout, campaign.position, 'callout');
  renderGuidanceArrow(callout, callout, normalizeGuidanceStyle(campaign.style));
  return callout;
}

/**
 * Render a light tour step placed on the page.
 *
 * @param {object} di - Topsail dependency container.
 * @param {Record<string, any>} campaign - Guidance campaign.
 * @returns {HTMLElement} Rendered tour or fallback banner.
 */
function renderTour(di, campaign, options = {}) {
  const steps = getTourSteps(campaign);
  const firstStep = steps[0];
  if (!firstStep) return renderBanner(di, campaign, options);

  clearCurrentSurface({ includeAnnouncementCenter: false });
  const backdrop = document.createElement('section');
  backdrop.className = 'guidance-tour-backdrop';
  backdrop.dataset.guidanceSurface = 'tour';
  backdrop.setAttribute('role', 'presentation');
  backdrop.innerHTML = `<section class="guidance-tour-card" role="dialog" aria-modal="false" aria-labelledby="guidanceTourTitle"></section>`;
  const card = backdrop.querySelector('.guidance-tour-card');
  document.body.appendChild(backdrop);

  let stepIndex = 0;
  const renderStep = () => {
    const step = steps[stepIndex] || firstStep;
    if (!card) return;
    card.innerHTML = buildGuidanceSurfaceMarkup(
      { ...campaign, title: step.title, body: step.body },
      {
        titleId: 'guidanceTourTitle',
        kicker: `Step ${stepIndex + 1} of ${steps.length}`,
        actions: `
        ${stepIndex > 0 ? '<button type="button" class="btn btn--secondary" data-guidance-tour-prev>Back</button>' : ''}
        ${campaign.type === 'onboarding' ? '<button type="button" class="btn btn--secondary" data-guidance-action="skip">Skip for now</button>' : ''}
        ${stepIndex === steps.length - 1 ? buildCtaMarkup(campaign) : ''}
        ${
          stepIndex === steps.length - 1
            ? '<button type="button" class="btn btn--primary" data-guidance-action="complete">Done</button>'
            : '<button type="button" class="btn btn--primary" data-guidance-tour-next>Next</button>'
        }
        `,
      }
    );
    applyGuidanceTreatment(card, campaign, { owner: backdrop });
    applyGuidancePosition(card, step.position, 'tour');
    renderGuidanceArrow(backdrop, card, normalizeGuidanceStyle(campaign.style));
  };

  backdrop.addEventListener('click', (event) => {
    if (event.target.closest('[data-guidance-tour-prev]')) {
      stepIndex = Math.max(0, stepIndex - 1);
      renderStep();
      return;
    }
    if (event.target.closest('[data-guidance-tour-next]')) {
      stepIndex = Math.min(steps.length - 1, stepIndex + 1);
      renderStep();
      return;
    }
    const button = event.target.closest('[data-guidance-action]');
    if (!button) return;
    const action = button.dataset.guidanceAction;
    void recordProgress(di, campaign.id, action).catch((error) => {
      logGuidanceProgressFailure(
        '[guidanceClient] failed to record progress',
        error
      );
    });
    removeGuidanceSurface(backdrop);
    if (typeof options.onAfterAction === 'function') {
      void options.onAfterAction({ action, campaign });
    }
  });

  renderStep();
  return backdrop;
}

/**
 * Render the onboarding modal.
 *
 * @param {object} di - Topsail dependency container.
 * @param {Record<string, any>} campaign - Guidance campaign.
 * @returns {HTMLElement} Rendered modal backdrop.
 */
function renderOnboarding(di, campaign, options = {}) {
  clearCurrentSurface({ includeAnnouncementCenter: false });
  const backdrop = document.createElement('section');
  backdrop.className = 'guidance-modal-backdrop';
  backdrop.dataset.guidanceSurface = 'onboarding';
  backdrop.setAttribute('role', 'presentation');
  backdrop.innerHTML = `
    <div class="guidance-modal" role="dialog" aria-modal="true" aria-labelledby="guidanceOnboardingTitle">
      ${buildGuidanceSurfaceMarkup(campaign, {
        titleId: 'guidanceOnboardingTitle',
        kicker: campaign.type === 'onboarding' ? 'Welcome' : '',
        actions: `
        <button type="button" class="btn btn--secondary" data-guidance-action="skip">Skip for now</button>
        ${buildCtaMarkup(campaign)}
        <button type="button" class="btn btn--primary" data-guidance-action="complete">Done</button>
        `,
      })}
    </div>
  `;
  bindSurfaceActions(backdrop, di, campaign, options);
  document.body.appendChild(backdrop);
  applyGuidanceTreatment(backdrop.querySelector('.guidance-modal'), campaign, {
    owner: backdrop,
  });
  applyGuidancePosition(
    backdrop.querySelector('.guidance-modal'),
    campaign.position,
    'onboarding'
  );
  renderGuidanceArrow(
    backdrop,
    backdrop.querySelector('.guidance-modal'),
    normalizeGuidanceStyle(campaign.style)
  );
  backdrop.querySelector('button')?.focus?.();
  return backdrop;
}

/**
 * Render the announcement center button and modal.
 *
 * @param {object} di - Topsail dependency container.
 * @param {Array<Record<string, any>>} campaigns - Announcement campaigns.
 * @returns {HTMLElement|null} Rendered button or null.
 */
function renderAnnouncementCenter(di, campaigns) {
  if (!campaigns.length) return null;
  const primaryCampaign = campaigns[0];
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn btn--secondary guidance-announcement-button';
  button.dataset.guidanceSurface = 'announcements';
  button.setAttribute('aria-label', "What's new");
  button.innerHTML = `
    <span>What's new</span>
    <span class="guidance-announcement-count">${campaigns.length}</span>
  `;

  button.addEventListener('click', () => {
    button.remove();
    document
      .querySelectorAll('[data-guidance-surface="announcement-list"]')
      .forEach((surface) => removeGuidanceSurface(surface));
    campaigns.forEach((campaign) => {
      void recordProgress(di, campaign.id, 'view').catch((error) => {
        logGuidanceProgressFailure(
          '[guidanceClient] failed to record announcement view',
          error
        );
      });
    });
    const backdrop = document.createElement('section');
    backdrop.className = 'guidance-modal-backdrop';
    backdrop.dataset.guidanceSurface = 'announcement-list';
    const primaryStyleCampaign = primaryCampaign || campaigns[0] || {};
    backdrop.innerHTML = `
      <div class="guidance-modal" role="dialog" aria-modal="true" aria-labelledby="guidanceAnnouncementsTitle">
        <header class="guidance-surface__header">
          <div>
            <p class="guidance-surface__eyebrow">Announcements</p>
            <h2 id="guidanceAnnouncementsTitle">What's new</h2>
          </div>
        </header>
        <div class="guidance-announcement-list">
          ${campaigns
            .map(
              (campaign) => `
              <article>
                <h3>${escapeHtml(campaign.title)}</h3>
                ${buildGuidanceBodyMarkup(campaign.body)}
                <div class="guidance-actions">
                  ${buildCtaMarkup(campaign)}
                  <button type="button" class="btn btn--secondary" data-guidance-dismiss="${escapeHtml(campaign.id)}">Dismiss</button>
                </div>
              </article>`
            )
            .join('')}
        </div>
        <footer class="guidance-actions guidance-surface__footer">
          <button type="button" class="btn btn--primary" data-guidance-close="1">Close</button>
        </footer>
      </div>
    `;
    backdrop.addEventListener('click', (event) => {
      const dismiss = event.target.closest('[data-guidance-dismiss]');
      if (dismiss) {
        void recordProgress(
          di,
          dismiss.dataset.guidanceDismiss,
          'dismiss'
        ).catch((error) => {
          logGuidanceProgressFailure(
            '[guidanceClient] failed to record announcement dismiss',
            error
          );
        });
        dismiss.closest('article')?.remove();
        return;
      }
      if (event.target.closest('[data-guidance-close]')) {
        removeGuidanceSurface(backdrop);
        document.body.appendChild(button);
      }
    });
    document.body.appendChild(backdrop);
    applyGuidanceTreatment(
      backdrop.querySelector('.guidance-modal'),
      primaryStyleCampaign,
      { owner: backdrop, spotlight: false }
    );
    applyGuidancePosition(
      backdrop.querySelector('.guidance-modal'),
      primaryStyleCampaign.position,
      'announcement_center'
    );
    renderGuidanceArrow(
      backdrop,
      backdrop.querySelector('.guidance-modal'),
      normalizeGuidanceStyle(primaryStyleCampaign.style)
    );
    backdrop.querySelector('button')?.focus?.();
  });

  document.body.appendChild(button);
  applyGuidancePosition(button, null, 'announcement_center');
  return button;
}

/**
 * Resolve a runtime display mode for an automatic campaign.
 *
 * @param {Record<string, any>} campaign - Candidate automatic campaign.
 * @returns {Promise<string>} Display mode.
 */
async function resolveReadyCampaignMode(campaign) {
  return getCampaignDisplayMode(campaign);
}

/**
 * Render automatic guidance campaigns one at a time.
 *
 * @param {object} di - Topsail dependency container.
 * @param {Array<Record<string, any>>} campaigns - Eligible campaigns.
 * @returns {Promise<HTMLElement|null>} First rendered surface or null.
 */
async function renderAutomaticCampaignQueue(di, campaigns) {
  const queue = getAutomaticCampaigns(campaigns);
  let index = 0;

  const renderNext = async () => {
    while (index < queue.length) {
      const campaign = queue[index];
      index += 1;
      const mode = await resolveReadyCampaignMode(campaign);
      if (!mode) continue;

      const options = {
        onAfterAction: () => renderNext(),
      };
      let surface = null;
      if (mode === 'onboarding')
        surface = renderOnboarding(di, campaign, options);
      else if (mode === 'tour') surface = renderTour(di, campaign, options);
      else if (mode === 'callout')
        surface = renderCallout(di, campaign, options);
      else surface = renderBanner(di, campaign, options);

      if (!surface) continue;
      await recordProgress(di, campaign.id, 'view').catch((error) => {
        logGuidanceProgressFailure(
          '[guidanceClient] failed to record view',
          error
        );
      });
      return surface;
    }
    return null;
  };

  return renderNext();
}

/**
 * Render eligible guidance campaigns.
 *
 * @param {object} di - Topsail dependency container.
 * @param {Array<Record<string, any>>} campaigns - Eligible campaigns.
 * @returns {Promise<void>} Resolves after rendering.
 */
async function renderGuidance(di, campaigns) {
  const announcements = getAnnouncementCenterCampaigns(campaigns);
  renderAnnouncementCenter(di, announcements);
  await renderAutomaticCampaignQueue(di, campaigns);
}

/**
 * Resolve campaigns from the backend for the current page.
 *
 * @param {object} di - Topsail dependency container.
 * @param {string} path - Current pathname.
 * @returns {Promise<Array<Record<string, any>>>} Eligible campaigns.
 */
async function resolveCampaigns(di, path) {
  const payload = await di.apiClient.fetchJson(
    di.endpoints.resolveGuidanceCampaigns(),
    {
      auth: true,
      body: { path },
    }
  );
  return Array.isArray(payload?.campaigns) ? payload.campaigns : [];
}

/**
 * Determine whether guidance startup failed because the optional campaign fetch was unavailable.
 *
 * @param {unknown} error - Error caught while starting runtime guidance.
 * @returns {boolean} True for browser fetch/network availability failures.
 */
function isGuidanceFetchUnavailable(error) {
  const message = String(error?.message || error || '');
  return /Failed to fetch|NetworkError|Load failed/i.test(message);
}

/**
 * Read the builder's style controls and current visual-placement state.
 *
 * @param {HTMLFormElement} form - Builder form.
 * @param {{ style?: Record<string, any> }} state - Builder state.
 * @returns {Record<string, any>} Normalized guidance style.
 */
function readBuilderStyle(form, state) {
  const currentStyle = normalizeGuidanceStyle(state.style);
  const nextStyle = normalizeGuidanceStyle({
    backgroundColor:
      form.querySelector('[name="guidanceBuilderBackgroundColor"]:checked')
        ?.value || currentStyle.backgroundColor,
    fontFamily:
      form.querySelector('[name="guidanceBuilderFontFamily"]')?.value ||
      currentStyle.fontFamily,
    fontSize:
      form.querySelector('[name="guidanceBuilderFontSize"]')?.value ||
      currentStyle.fontSize,
    size: currentStyle.size,
    dimensions: currentStyle.dimensions,
    arrow: {
      enabled:
        form.querySelector('[name="guidanceBuilderArrowEnabled"]')?.checked ===
        true,
      side: currentStyle.arrow.side,
      offset: currentStyle.arrow.offset,
      target: currentStyle.arrow.target,
    },
    spotlight: {
      ...currentStyle.spotlight,
      enabled:
        form.querySelector('[name="guidanceBuilderSpotlightEnabled"]')
          ?.checked === true,
      size:
        form.querySelector('[name="guidanceBuilderSpotlightSize"]')?.value ||
        currentStyle.spotlight.size,
    },
  });
  state.style = nextStyle;
  return nextStyle;
}

/**
 * Read the campaign payload from the in-page builder form.
 *
 * @param {HTMLFormElement} form - Builder form.
 * @param {string} pathname - Current page path.
 * @param {{ campaignId: string, tourSteps: Array<Record<string, any>>, position: { x: number, y: number }, style: Record<string, any> }} state - Builder state.
 * @returns {Record<string, any>} Campaign payload.
 */
function readBuilderPayload(form, pathname, state) {
  const roles = Array.from(
    form.querySelectorAll('[name="guidanceBuilderRole"]:checked')
  ).map((input) => input.value);
  return buildBuilderCampaignPayload(
    {
      campaignId: state.campaignId,
      title: form.querySelector('[name="guidanceBuilderTitle"]')?.value || '',
      type: form.querySelector('[name="guidanceBuilderType"]')?.value || '',
      placement:
        form.querySelector('[name="guidanceBuilderPlacement"]')?.value || '',
      body: form.querySelector('[name="guidanceBuilderBody"]')?.value || '',
      ctaLabel:
        form.querySelector('[name="guidanceBuilderCtaLabel"]')?.value || '',
      ctaUrl: form.querySelector('[name="guidanceBuilderCtaUrl"]')?.value || '',
      startsAt:
        form.querySelector('[name="guidanceBuilderStartsAt"]')?.value || '',
      endsAt: form.querySelector('[name="guidanceBuilderEndsAt"]')?.value || '',
      tenantIds:
        form.querySelector('[name="guidanceBuilderTenantIds"]')?.value || '',
      roles,
      steps: state.tourSteps,
      position: state.position,
      style: readBuilderStyle(form, state),
    },
    pathname
  );
}

/**
 * Set the builder status message.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {string} message - Status text.
 * @param {'neutral'|'success'|'error'} [tone='neutral'] - Status tone.
 * @returns {void}
 */
function setBuilderStatus(panel, message, tone = 'neutral') {
  const status = panel.querySelector('[data-guidance-builder-status]');
  if (!status) return;
  status.textContent = message;
  status.dataset.tone = tone;
}

/**
 * Render the in-page builder's ordered tour step list.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {Array<Record<string, any>>} steps - Ordered tour steps.
 * @returns {void}
 */
function renderBuilderTourSteps(panel, steps) {
  const list = panel.querySelector('[data-guidance-tour-step-list]');
  if (!list) return;
  if (!steps.length) {
    list.innerHTML =
      '<p class="guidance-builder-empty">No tour steps added.</p>';
    return;
  }
  list.innerHTML = steps
    .map(
      (step, index) => `
        <article class="guidance-builder-tour-step">
          <div>
            <strong>${index + 1}. ${escapeHtml(step.title)}</strong>
            <span>${escapeHtml(formatGuidancePosition(step.position))}</span>
          </div>
          <button type="button" class="btn btn--secondary" data-guidance-remove-tour-step="${index}">Remove</button>
        </article>`
    )
    .join('');
}

/**
 * Read the current builder fields as a reusable tour step.
 *
 * @param {HTMLFormElement} form - Builder form.
 * @param {{ position: { x: number, y: number } }} state - Builder state.
 * @returns {{ step: { title: string, body: string, position: { x: number, y: number } }|null, error: string }} Step or validation error.
 */
function readCurrentBuilderTourStep(form, state) {
  const step = {
    title:
      form.querySelector('[name="guidanceBuilderTitle"]')?.value.trim() || '',
    body:
      form.querySelector('[name="guidanceBuilderBody"]')?.value.trim() || '',
    position: normalizeGuidancePosition(
      state.position,
      getDefaultGuidancePosition('tour')
    ),
  };
  if (!step.title) return { step: null, error: 'Add a step title first.' };
  if (!step.body) return { step: null, error: 'Add step body copy first.' };
  return { step, error: '' };
}

/**
 * Remove builder mode from the visible URL.
 *
 * @returns {void}
 */
function removeBuilderSearchParam() {
  const url = new URL(window.location.href);
  url.searchParams.delete('guidanceBuilder');
  window.history.replaceState(
    {},
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
}

/**
 * Update builder state with a dragged preview position.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {{ position: { x: number, y: number }, positionTouched?: boolean }} state - Builder state.
 * @param {unknown} position - Candidate position.
 * @returns {{ x: number, y: number }} Normalized position.
 */
function setBuilderPosition(panel, state, position) {
  state.position = normalizeGuidancePosition(position, state.position);
  state.positionTouched = true;
  setBuilderStatus(
    panel,
    `Placement saved in draft state: ${formatGuidancePosition(state.position)}.`,
    'success'
  );
  return state.position;
}

/**
 * Update the stored position for a tour preview step.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {{ tourSteps: Array<Record<string, any>>, position: { x: number, y: number } }} state - Builder state.
 * @param {number} stepIndex - Preview step index.
 * @param {unknown} position - Candidate position.
 * @returns {void}
 */
function setBuilderTourStepPosition(panel, state, stepIndex, position) {
  if (stepIndex < state.tourSteps.length) {
    state.tourSteps[stepIndex] = {
      ...state.tourSteps[stepIndex],
      position: normalizeGuidancePosition(
        position,
        state.tourSteps[stepIndex].position
      ),
    };
    renderBuilderTourSteps(panel, state.tourSteps);
    setBuilderStatus(
      panel,
      `Step ${stepIndex + 1} placement updated.`,
      'success'
    );
    return;
  }
  setBuilderPosition(panel, state, position);
}

/**
 * Update the builder style with a dragged spotlight center.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {{ style: Record<string, any> }} state - Builder state.
 * @param {unknown} position - Candidate spotlight center position.
 * @returns {void}
 */
function setBuilderSpotlightPosition(panel, state, position) {
  const currentStyle = normalizeGuidanceStyle(state.style);
  const spotlightToggle = panel.querySelector(
    '[name="guidanceBuilderSpotlightEnabled"]'
  );
  if (spotlightToggle) spotlightToggle.checked = true;
  state.style = normalizeGuidanceStyle({
    ...currentStyle,
    spotlight: {
      ...currentStyle.spotlight,
      ...normalizeGuidancePosition(position, currentStyle.spotlight),
      enabled: true,
    },
  });
  setBuilderStatus(panel, 'Circle placement updated.', 'success');
}

/**
 * Update the builder style with a resized guidance surface.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {{ style: Record<string, any> }} state - Builder state.
 * @param {{ width: number, height: number }} dimensions - Exact surface dimensions.
 * @returns {void}
 */
function setBuilderDimensions(panel, state, dimensions) {
  const currentStyle = normalizeGuidanceStyle(state.style);
  state.style = normalizeGuidanceStyle({
    ...currentStyle,
    dimensions,
  });
  const readout = panel.querySelector('[data-guidance-size-readout]');
  if (readout) {
    readout.textContent = `${dimensions.width} x ${dimensions.height}`;
  }
  setBuilderStatus(
    panel,
    `Size updated to ${dimensions.width} x ${dimensions.height}.`,
    'success'
  );
}

/**
 * Update the builder style with a dragged arrow endpoint.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {{ style: Record<string, any> }} state - Builder state.
 * @param {unknown} target - Candidate arrow target position.
 * @returns {void}
 */
function setBuilderArrowTarget(panel, state, target) {
  const currentStyle = normalizeGuidanceStyle(state.style);
  const arrowToggle = panel.querySelector(
    '[name="guidanceBuilderArrowEnabled"]'
  );
  if (arrowToggle) arrowToggle.checked = true;
  state.style = normalizeGuidanceStyle({
    ...currentStyle,
    arrow: {
      ...currentStyle.arrow,
      enabled: true,
      target,
    },
  });
  setBuilderStatus(panel, 'Arrow target updated.', 'success');
}

/**
 * Create a preview campaign with fallback copy for incomplete drafts.
 *
 * @param {Record<string, any>} campaign - Builder payload.
 * @returns {Record<string, any>} Preview-safe campaign.
 */
function buildBuilderPreviewCampaign(campaign) {
  return {
    ...campaign,
    id: campaign.id || 'builder-preview',
    title: campaign.title || 'Untitled campaign',
    body: campaign.body || 'Draft guidance copy appears here.',
  };
}

/**
 * Apply placement and drag behavior to a builder preview surface.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {HTMLElement} surface - Preview surface.
 * @param {{ position: { x: number, y: number } }} state - Builder state.
 * @param {unknown} position - Stored position.
 * @param {string} placement - Campaign placement.
 * @param {number|null} [tourStepIndex=null] - Optional tour step index.
 * @param {Record<string, any>|null} [campaign=null] - Preview campaign.
 * @param {HTMLElement|null} [owner=null] - Preview owner.
 * @returns {void}
 */
function placeBuilderPreviewSurface(
  panel,
  surface,
  state,
  position,
  placement,
  tourStepIndex = null,
  campaign = null,
  owner = null
) {
  applyGuidancePosition(surface, position, placement);
  enableGuidanceSurfaceDragging(surface, (nextPosition) => {
    if (Number.isInteger(tourStepIndex)) {
      setBuilderTourStepPosition(panel, state, tourStepIndex, nextPosition);
      return;
    }
    setBuilderPosition(panel, state, nextPosition);
  });
  if (campaign) {
    enableGuidanceSurfaceResizing(surface, (dimensions) => {
      setBuilderDimensions(panel, state, dimensions);
    });
    renderGuidanceArrow(
      owner || surface,
      surface,
      normalizeGuidanceStyle(campaign.style),
      {
        draggable: true,
        onTargetChange: (target) => setBuilderArrowTarget(panel, state, target),
      }
    );
  }
}

/**
 * Render shared preview action markup.
 *
 * @param {Record<string, any>} campaign - Preview campaign.
 * @param {string} [primaryLabel='Got it'] - Primary action copy.
 * @returns {string} Preview actions.
 */
function buildBuilderPreviewActions(campaign, primaryLabel = 'Got it') {
  return `
    ${buildCtaMarkup(campaign)}
    <button type="button" class="btn btn--secondary">${escapeHtml(primaryLabel)}</button>
  `;
}

/**
 * Render a non-recording draggable preview surface for the builder payload.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {Record<string, any>} campaign - Campaign payload.
 * @param {{ position: { x: number, y: number }, previewStepIndex?: number, style: Record<string, any>, tourSteps: Array<Record<string, any>> }} state - Builder state.
 * @returns {HTMLElement} Rendered preview surface.
 */
function renderBuilderPreview(panel, campaign, state) {
  const previewCampaign = buildBuilderPreviewCampaign(campaign);
  clearCurrentSurface();

  const mode = getCampaignDisplayMode(previewCampaign);

  if (mode === 'onboarding') {
    const backdrop = document.createElement('section');
    backdrop.className = 'guidance-modal-backdrop guidance-builder-preview';
    backdrop.dataset.guidanceSurface = 'builder-preview';
    backdrop.innerHTML = `
      <div class="guidance-modal" role="dialog" aria-modal="true" aria-labelledby="guidanceBuilderPreviewTitle">
        ${buildGuidanceSurfaceMarkup(previewCampaign, {
          titleId: 'guidanceBuilderPreviewTitle',
          kicker: previewCampaign.type === 'onboarding' ? 'Welcome' : '',
          actions: buildBuilderPreviewActions(previewCampaign, 'Done'),
        })}
      </div>
    `;
    document.body.appendChild(backdrop);
    applyGuidanceTreatment(
      backdrop.querySelector('.guidance-modal'),
      previewCampaign,
      {
        owner: backdrop,
        spotlightDraggable: true,
        onSpotlightPositionChange: (position) =>
          setBuilderSpotlightPosition(panel, state, position),
      }
    );
    placeBuilderPreviewSurface(
      panel,
      backdrop.querySelector('.guidance-modal'),
      state,
      previewCampaign.position,
      'onboarding',
      null,
      previewCampaign,
      backdrop
    );
    return backdrop;
  }

  if (mode === 'announcement_center') {
    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      'btn btn--secondary guidance-announcement-button guidance-announcement-button--preview guidance-builder-preview';
    button.dataset.guidanceSurface = 'builder-preview';
    button.textContent = "What's new";
    button.addEventListener('click', () => {
      document
        .querySelectorAll('[data-guidance-surface="builder-preview-modal"]')
        .forEach((surface) => removeGuidanceSurface(surface));
      const backdrop = document.createElement('section');
      backdrop.className = 'guidance-modal-backdrop guidance-builder-preview';
      backdrop.dataset.guidanceSurface = 'builder-preview-modal';
      backdrop.innerHTML = `
        <div class="guidance-modal" role="dialog" aria-modal="true" aria-labelledby="guidanceBuilderAnnouncementPreviewTitle">
          ${buildGuidanceSurfaceMarkup(previewCampaign, {
            titleId: 'guidanceBuilderAnnouncementPreviewTitle',
            kicker: 'Announcements',
            actions: buildBuilderPreviewActions(previewCampaign, 'Close'),
          })}
        </div>
      `;
      document.body.appendChild(backdrop);
      applyGuidanceTreatment(
        backdrop.querySelector('.guidance-modal'),
        previewCampaign,
        {
          owner: backdrop,
          spotlightDraggable: true,
          onSpotlightPositionChange: (position) =>
            setBuilderSpotlightPosition(panel, state, position),
        }
      );
      placeBuilderPreviewSurface(
        panel,
        backdrop.querySelector('.guidance-modal'),
        state,
        previewCampaign.position,
        'announcement_center',
        null,
        previewCampaign,
        backdrop
      );
      backdrop.addEventListener('click', (event) => {
        if (event.target.closest('.guidance-surface__footer .btn')) {
          removeGuidanceSurface(backdrop);
        }
      });
    });
    document.body.appendChild(button);
    applyGuidancePosition(button, null, 'announcement_center');
    button.click();
    return button;
  }

  if (mode === 'tour') {
    const previewSteps = getTourSteps(previewCampaign);
    const backdrop = document.createElement('section');
    backdrop.className = 'guidance-tour-backdrop guidance-builder-preview';
    backdrop.dataset.guidanceSurface = 'builder-preview';
    backdrop.innerHTML = `<section class="guidance-tour-card" role="dialog" aria-modal="false" aria-labelledby="guidanceBuilderTourPreviewTitle"></section>`;
    const card = backdrop.querySelector('.guidance-tour-card');
    state.previewStepIndex = Math.min(
      Math.max(Number(state.previewStepIndex) || 0, 0),
      Math.max(0, previewSteps.length - 1)
    );

    const renderPreviewStep = () => {
      const step = previewSteps[state.previewStepIndex] || previewSteps[0];
      if (!card || !step) return;
      card.innerHTML = `
        ${buildGuidanceSurfaceMarkup(
          { ...previewCampaign, title: step.title, body: step.body },
          {
            titleId: 'guidanceBuilderTourPreviewTitle',
            kicker: `Step ${state.previewStepIndex + 1} of ${previewSteps.length}`,
            actions: `
          ${state.previewStepIndex > 0 ? '<button type="button" class="btn btn--secondary" data-guidance-tour-prev>Back</button>' : ''}
          ${
            state.previewStepIndex === previewSteps.length - 1
              ? '<button type="button" class="btn btn--primary">Done</button>'
              : '<button type="button" class="btn btn--primary" data-guidance-tour-next>Next</button>'
          }
            `,
          }
        )}
      `;
      applyGuidanceTreatment(card, previewCampaign, {
        owner: backdrop,
        spotlightDraggable: true,
        onSpotlightPositionChange: (position) =>
          setBuilderSpotlightPosition(panel, state, position),
      });
      applyGuidancePosition(card, step.position, 'tour');
      enableGuidanceSurfaceResizing(card, (dimensions) => {
        setBuilderDimensions(panel, state, dimensions);
      });
      renderGuidanceArrow(
        backdrop,
        card,
        normalizeGuidanceStyle(previewCampaign.style),
        {
          draggable: true,
          onTargetChange: (target) =>
            setBuilderArrowTarget(panel, state, target),
        }
      );
      if (card.dataset.guidanceTourDragBound !== 'true') {
        card.dataset.guidanceTourDragBound = 'true';
        enableGuidanceSurfaceDragging(card, (nextPosition) => {
          setBuilderTourStepPosition(
            panel,
            state,
            state.previewStepIndex,
            nextPosition
          );
        });
      }
    };

    backdrop.addEventListener('click', (event) => {
      if (event.target.closest('[data-guidance-tour-prev]')) {
        state.previewStepIndex = Math.max(0, state.previewStepIndex - 1);
        renderPreviewStep();
        return;
      }
      if (event.target.closest('[data-guidance-tour-next]')) {
        state.previewStepIndex = Math.min(
          previewSteps.length - 1,
          state.previewStepIndex + 1
        );
        renderPreviewStep();
      }
    });
    document.body.appendChild(backdrop);
    renderPreviewStep();
    return backdrop;
  }

  const surface = document.createElement('section');
  surface.className =
    mode === 'callout'
      ? 'guidance-callout guidance-builder-preview'
      : 'guidance-banner guidance-builder-preview';
  surface.dataset.guidanceSurface = 'builder-preview';
  surface.innerHTML = `
    ${buildGuidanceSurfaceMarkup(previewCampaign, {
      actions: `
      ${buildBuilderPreviewActions(previewCampaign, mode === 'callout' ? 'Got it' : 'Dismiss')}
      `,
    })}
  `;
  document.body.appendChild(surface);
  applyGuidanceTreatment(surface, previewCampaign, {
    spotlightDraggable: true,
    onSpotlightPositionChange: (position) =>
      setBuilderSpotlightPosition(panel, state, position),
  });
  placeBuilderPreviewSurface(
    panel,
    surface,
    state,
    previewCampaign.position,
    mode,
    null,
    previewCampaign,
    surface
  );
  return surface;
}

/**
 * Persist the builder form as a draft campaign.
 *
 * @param {object} di - Topsail dependency container.
 * @param {HTMLElement} panel - Builder panel.
 * @param {string} pathname - Current page path.
 * @param {{ campaignId: string }} state - Builder state.
 * @returns {Promise<string>} Saved campaign id, or an empty string.
 */
async function saveBuilderDraft(di, panel, pathname, state) {
  const form = panel.querySelector('[data-guidance-builder-form]');
  const payload = readBuilderPayload(form, pathname, state);
  const validationError = validateBuilderCampaignPayload(payload);
  if (validationError) {
    setBuilderStatus(panel, validationError, 'error');
    return '';
  }

  setBuilderStatus(panel, 'Saving draft...');
  const result = await di.apiClient.fetchJson(
    di.endpoints.saveGuidanceCampaignDraft(),
    {
      auth: true,
      body: payload,
    }
  );
  state.campaignId = result?.campaign?.id || state.campaignId;
  setBuilderStatus(panel, 'Draft saved.', 'success');
  return state.campaignId;
}

/**
 * Publish the builder campaign after saving its latest draft.
 *
 * @param {object} di - Topsail dependency container.
 * @param {HTMLElement} panel - Builder panel.
 * @param {string} pathname - Current page path.
 * @param {{ campaignId: string }} state - Builder state.
 * @returns {Promise<void>} Resolves once published.
 */
async function publishBuilderCampaign(di, panel, pathname, state) {
  const campaignId = await saveBuilderDraft(di, panel, pathname, state);
  if (!campaignId) return;
  setBuilderStatus(panel, 'Publishing campaign...');
  await di.apiClient.fetchJson(di.endpoints.publishGuidanceCampaign(), {
    auth: true,
    body: { campaignId },
  });
  setBuilderStatus(panel, 'Campaign published.', 'success');
}

/**
 * Clamp a floating builder panel position to the visible viewport.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {number} left - Proposed left coordinate.
 * @param {number} top - Proposed top coordinate.
 * @param {{ innerWidth?: number, innerHeight?: number }} [viewport=window] - Viewport dimensions.
 * @returns {{ left: number, top: number }} Clamped position.
 */
function clampBuilderPanelPosition(panel, left, top, viewport = window) {
  const margin = 12;
  const rect =
    typeof panel?.getBoundingClientRect === 'function'
      ? panel.getBoundingClientRect()
      : {};
  const viewportWidth =
    Number(viewport?.innerWidth) ||
    document.documentElement?.clientWidth ||
    1024;
  const viewportHeight =
    Number(viewport?.innerHeight) ||
    document.documentElement?.clientHeight ||
    768;
  const panelWidth = Math.min(
    Number(rect.width) || panel?.offsetWidth || 448,
    viewportWidth - margin * 2
  );
  const panelHeight = Math.min(
    Number(rect.height) || panel?.offsetHeight || 600,
    viewportHeight - margin * 2
  );
  const maxLeft = Math.max(margin, viewportWidth - panelWidth - margin);
  const maxTop = Math.max(margin, viewportHeight - panelHeight - margin);

  return {
    left: Math.min(Math.max(Number(left) || margin, margin), maxLeft),
    top: Math.min(Math.max(Number(top) || margin, margin), maxTop),
  };
}

/**
 * Enable desktop pointer dragging for the in-page builder panel.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @returns {void}
 */
function enableBuilderPanelDragging(panel) {
  const handle = panel.querySelector('[data-guidance-builder-drag-handle]');
  if (!handle) return;

  let dragState = null;
  function stopDrag() {
    dragState = null;
    panel.classList.remove('guidance-builder-panel--dragging');
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', stopDrag);
    document.removeEventListener('pointercancel', stopDrag);
  }
  function onPointerMove(event) {
    if (!dragState) return;
    const nextPosition = clampBuilderPanelPosition(
      panel,
      event.clientX - dragState.offsetX,
      event.clientY - dragState.offsetY
    );
    panel.style.left = `${nextPosition.left}px`;
    panel.style.top = `${nextPosition.top}px`;
    panel.style.right = 'auto';
  }

  handle.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 && event.pointerType !== 'touch') return;
    if (window.matchMedia?.('(max-width: 640px)')?.matches) return;
    if (
      event.target.closest('a, button, input, label, select, summary, textarea')
    ) {
      return;
    }

    const rect = panel.getBoundingClientRect();
    dragState = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    panel.style.left = `${rect.left}px`;
    panel.style.top = `${rect.top}px`;
    panel.style.right = 'auto';
    panel.classList.add('guidance-builder-panel--dragging');
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', stopDrag);
    document.addEventListener('pointercancel', stopDrag);
    event.preventDefault();
  });
}

/**
 * Read the selected builder placement.
 *
 * @param {HTMLFormElement} form - Builder form.
 * @returns {string} Selected placement.
 */
function getBuilderPlacement(form) {
  return (
    form.querySelector('[name="guidanceBuilderPlacement"]')?.value || 'banner'
  );
}

/**
 * Reset builder placement state to the selected placement default.
 *
 * @param {HTMLFormElement} form - Builder form.
 * @param {{ position: { x: number, y: number }, positionTouched?: boolean, previewStepIndex?: number }} state - Builder state.
 * @returns {void}
 */
function resetBuilderPositionForPlacement(form, state) {
  state.position = getDefaultGuidancePosition(getBuilderPlacement(form));
  state.positionTouched = false;
  state.previewStepIndex = 0;
}

/**
 * Render the latest builder payload as a live draggable preview.
 *
 * @param {HTMLElement} panel - Builder panel.
 * @param {string} pathname - Current page path.
 * @param {{ campaignId: string, tourSteps: Array<Record<string, any>>, position: { x: number, y: number }, style: Record<string, any> }} state - Builder state.
 * @returns {HTMLElement} Rendered preview surface.
 */
function refreshBuilderPreview(panel, pathname, state) {
  const form = panel.querySelector('[data-guidance-builder-form]');
  return renderBuilderPreview(
    panel,
    readBuilderPayload(form, pathname, state),
    state
  );
}

/**
 * Mount the superadmin in-page guidance builder.
 *
 * @param {object} di - Topsail dependency container.
 * @param {string} pathname - Current page path.
 * @returns {HTMLElement} Builder panel.
 */
function mountGuidanceBuilder(di, pathname) {
  document.querySelector('[data-guidance-builder="panel"]')?.remove();
  clearCurrentSurface();

  const state = {
    campaignId: '',
    position: getDefaultGuidancePosition('banner'),
    positionTouched: false,
    previewStepIndex: 0,
    style: normalizeGuidanceStyle(),
    tourSteps: [],
  };
  const panel = document.createElement('aside');
  panel.className = 'guidance-builder-panel';
  panel.dataset.guidanceBuilder = 'panel';
  panel.setAttribute('aria-label', 'Guidance builder');
  panel.innerHTML = `
    <form class="guidance-builder-form" data-guidance-builder-form>
      <header class="guidance-builder-header" data-guidance-builder-drag-handle>
        <div>
          <p class="guidance-builder-eyebrow">Guidance builder</p>
          <h2>Build on this page</h2>
        </div>
        <div class="guidance-builder-header-actions">
          <a class="btn btn--secondary" href="/admin/guidance-campaigns.html">Back to campaigns</a>
          <button type="button" class="btn btn--secondary" data-guidance-builder-close>Close</button>
        </div>
      </header>

      <label class="guidance-builder-field">
        <span>Page</span>
        <input class="form-control" type="text" value="${escapeHtml(pathname)}" readonly />
      </label>

      <label class="guidance-builder-field">
        <span>Title</span>
        <input class="form-control" name="guidanceBuilderTitle" type="text" maxlength="120" required />
      </label>

      <label class="guidance-builder-field">
        <span>Message</span>
        <textarea class="form-control" name="guidanceBuilderBody" maxlength="900" required></textarea>
      </label>

      <div class="guidance-builder-grid">
        <label class="guidance-builder-field">
          <span>Type</span>
          <select class="form-control" name="guidanceBuilderType">
            <option value="announcement">Announcement</option>
            <option value="onboarding">Onboarding</option>
          </select>
        </label>
        <label class="guidance-builder-field">
          <span>Placement</span>
          <select class="form-control" name="guidanceBuilderPlacement">
            <option value="banner">Banner</option>
            <option value="callout">Contextual callout</option>
            <option value="tour">Light tour</option>
            <option value="announcement_center">Announcement center</option>
          </select>
        </label>
      </div>

      <details class="guidance-builder-advanced guidance-builder-style" open>
        <summary>Style</summary>
        <fieldset class="guidance-builder-swatch-group">
          <legend>Background</legend>
          ${Object.entries(GUIDANCE_STYLE_PRESETS)
            .map(
              ([value, preset]) => `
                <label class="guidance-builder-swatch" data-guidance-swatch="${escapeHtml(value)}">
                  <input name="guidanceBuilderBackgroundColor" type="radio" value="${escapeHtml(value)}" ${value === GUIDANCE_STYLE_DEFAULTS.backgroundColor ? 'checked' : ''} />
                  <span>${escapeHtml(preset.label)}</span>
                </label>`
            )
            .join('')}
        </fieldset>
        <div class="guidance-builder-grid">
          <label class="guidance-builder-field">
            <span>Font</span>
            <select class="form-control" name="guidanceBuilderFontFamily">
              <option value="brand">Brand display</option>
              <option value="system">System</option>
              <option value="serif">Serif</option>
              <option value="mono">Mono</option>
            </select>
          </label>
          <label class="guidance-builder-field">
            <span>Text size</span>
            <select class="form-control" name="guidanceBuilderFontSize">
              <option value="sm">Small</option>
              <option value="md" selected>Medium</option>
              <option value="lg">Large</option>
              <option value="xl">Extra large</option>
            </select>
          </label>
          <div class="guidance-builder-field">
            <span>Size</span>
            <output class="guidance-builder-size-readout" data-guidance-size-readout>Auto</output>
          </div>
        </div>
        <div class="guidance-builder-toggles">
          <label>
            <input name="guidanceBuilderArrowEnabled" type="checkbox" />
            Add arrow
          </label>
          <label>
            <input name="guidanceBuilderSpotlightEnabled" type="checkbox" />
            Add circle
          </label>
        </div>
        <label class="guidance-builder-field">
          <span>Circle size</span>
          <input
            class="form-control"
            name="guidanceBuilderSpotlightSize"
            type="range"
            min="6"
            max="80"
            step="1"
            value="${GUIDANCE_STYLE_DEFAULTS.spotlight.size}"
          />
        </label>
      </details>

      <div class="guidance-builder-grid">
        <label class="guidance-builder-field">
          <span>CTA label</span>
          <input class="form-control" name="guidanceBuilderCtaLabel" type="text" />
        </label>
        <label class="guidance-builder-field">
          <span>CTA URL</span>
          <input class="form-control" name="guidanceBuilderCtaUrl" type="text" placeholder="/reports.html" />
        </label>
      </div>

      <div class="guidance-builder-grid guidance-builder-date-grid">
        <label class="guidance-builder-field">
          <span>Starts at</span>
          <input class="form-control" name="guidanceBuilderStartsAt" type="datetime-local" />
        </label>
        <label class="guidance-builder-field">
          <span>Ends at</span>
          <input class="form-control" name="guidanceBuilderEndsAt" type="datetime-local" />
        </label>
      </div>

      <section class="guidance-builder-tour-steps" aria-label="Tour steps">
        <header>
          <span>Tour steps</span>
          <button type="button" class="btn btn--secondary" data-guidance-add-tour-step>Add step</button>
        </header>
        <div class="guidance-builder-tour-step-list" data-guidance-tour-step-list></div>
      </section>

      <details class="guidance-builder-advanced">
        <summary>Audience</summary>
        <label class="guidance-builder-field">
          <span>Tenant IDs</span>
          <textarea class="form-control" name="guidanceBuilderTenantIds" placeholder="Blank targets all tenants"></textarea>
        </label>
        <div class="guidance-builder-roles" aria-label="Target roles">
          <label><input name="guidanceBuilderRole" type="checkbox" value="all" checked /> All</label>
          <label><input name="guidanceBuilderRole" type="checkbox" value="seller" /> Sellers</label>
          <label><input name="guidanceBuilderRole" type="checkbox" value="manager" /> Managers</label>
          <label><input name="guidanceBuilderRole" type="checkbox" value="admin" /> Admins</label>
        </div>
      </details>

      <div class="guidance-builder-actions">
        <button type="button" class="btn btn--secondary" data-guidance-reset-position>Reset position</button>
        <button type="button" class="btn btn--secondary" data-guidance-save-draft>Save draft</button>
        <button type="button" class="btn btn--primary" data-guidance-publish>Publish</button>
      </div>
      <p class="guidance-builder-status" data-guidance-builder-status role="status" aria-live="polite"></p>
    </form>
  `;

  enableBuilderPanelDragging(panel);

  let previewFrame = 0;
  const schedulePreview = () => {
    if (previewFrame) return;
    const scheduleFrame =
      globalThis.requestAnimationFrame ||
      ((callback) => {
        callback();
        return 0;
      });
    previewFrame = scheduleFrame(() => {
      previewFrame = 0;
      refreshBuilderPreview(panel, pathname, state);
    });
  };

  panel.addEventListener('click', (event) => {
    if (event.target.closest('[data-guidance-builder-close]')) {
      panel.remove();
      clearCurrentSurface();
      removeBuilderSearchParam();
      return;
    }
    if (event.target.closest('[data-guidance-reset-position]')) {
      const form = panel.querySelector('[data-guidance-builder-form]');
      resetBuilderPositionForPlacement(form, state);
      refreshBuilderPreview(panel, pathname, state);
      setBuilderStatus(panel, 'Placement reset.', 'success');
      return;
    }
    if (event.target.closest('[data-guidance-add-tour-step]')) {
      const form = panel.querySelector('[data-guidance-builder-form]');
      const placement =
        form.querySelector('[name="guidanceBuilderPlacement"]')?.value || '';
      if (placement !== 'tour') {
        setBuilderStatus(
          panel,
          'Choose Light tour before adding steps.',
          'error'
        );
        return;
      }
      const { step, error } = readCurrentBuilderTourStep(form, state);
      if (error) {
        setBuilderStatus(panel, error, 'error');
        return;
      }
      state.tourSteps.push(step);
      state.previewStepIndex = state.tourSteps.length - 1;
      renderBuilderTourSteps(panel, state.tourSteps);
      refreshBuilderPreview(panel, pathname, state);
      setBuilderStatus(
        panel,
        `Added step ${state.tourSteps.length}.`,
        'success'
      );
      return;
    }
    const removeStepButton = event.target.closest(
      '[data-guidance-remove-tour-step]'
    );
    if (removeStepButton) {
      const index = Number(removeStepButton.dataset.guidanceRemoveTourStep);
      if (Number.isInteger(index)) {
        state.tourSteps.splice(index, 1);
        state.previewStepIndex = Math.min(
          state.previewStepIndex,
          Math.max(0, state.tourSteps.length - 1)
        );
        renderBuilderTourSteps(panel, state.tourSteps);
        refreshBuilderPreview(panel, pathname, state);
        setBuilderStatus(panel, 'Tour step removed.', 'success');
      }
      return;
    }
    if (event.target.closest('[data-guidance-save-draft]')) {
      void saveBuilderDraft(di, panel, pathname, state).catch((error) => {
        console.warn('[guidanceClient] builder draft save failed', error);
        setBuilderStatus(
          panel,
          error.message || 'Unable to save draft.',
          'error'
        );
      });
      return;
    }
    if (event.target.closest('[data-guidance-publish]')) {
      void publishBuilderCampaign(di, panel, pathname, state).catch((error) => {
        console.warn('[guidanceClient] builder publish failed', error);
        setBuilderStatus(panel, error.message || 'Unable to publish.', 'error');
      });
    }
  });

  panel.addEventListener('input', (event) => {
    if (event.target.closest('[data-guidance-builder-form]')) schedulePreview();
  });
  panel.addEventListener('change', (event) => {
    if (event.target.matches('[name="guidanceBuilderPlacement"]')) {
      const form = panel.querySelector('[data-guidance-builder-form]');
      resetBuilderPositionForPlacement(form, state);
    }
    schedulePreview();
  });

  document.body.appendChild(panel);
  renderBuilderTourSteps(panel, state.tourSteps);
  setBuilderStatus(panel, 'Builder ready.');
  refreshBuilderPreview(panel, pathname, state);
  panel.querySelector('[name="guidanceBuilderTitle"]')?.focus?.();
  return panel;
}

/**
 * Mount the builder only when the signed-in user is a superadmin.
 *
 * @param {object} di - Topsail dependency container.
 * @param {string} pathname - Current page path.
 * @returns {Promise<boolean>} True when builder mode took over the page.
 */
async function startGuidanceBuilderIfAllowed(di, pathname) {
  try {
    await di.apiClient.fetchJson(di.endpoints.listGuidanceCampaigns(), {
      auth: true,
      method: 'GET',
    });
    mountGuidanceBuilder(di, pathname);
    return true;
  } catch (error) {
    console.warn('[guidanceClient] builder unavailable', error);
    return false;
  }
}

/**
 * Start the runtime guidance client once for an app page.
 *
 * @param {object} di - Topsail dependency container.
 * @param {{ pathname?: string }} [options={}] - Optional runtime overrides.
 * @returns {void}
 */
export function startGuidanceClient(di, options = {}) {
  if (guidanceStarted) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__DISABLE_GUIDANCE === true) return;

  const pathname = options.pathname || window.location?.pathname || '/';
  const search = options.search ?? window.location?.search ?? '';
  const builderMode = isGuidanceBuilderMode(search);
  if (!shouldRunGuidanceOnPath(pathname, { builderMode })) return;
  if (!di?.auth?.waitForLogin || !di?.apiClient || !di?.endpoints) return;

  guidanceStarted = true;
  const run = async () => {
    try {
      await di.auth.waitForLogin(5_000);
      if (builderMode && (await startGuidanceBuilderIfAllowed(di, pathname))) {
        return;
      }
      const campaigns = await resolveCampaigns(di, pathname);
      await renderGuidance(di, campaigns);
    } catch (error) {
      if (isGuidanceFetchUnavailable(error)) {
        console.debug('[guidanceClient] guidance unavailable', {
          message: error?.message || String(error),
        });
        return;
      }
      console.warn('[guidanceClient] guidance unavailable', error);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void run();
    });
  } else {
    void run();
  }
}

export const __testables = {
  buildBuilderCampaignPayload,
  buildCtaMarkup,
  clampBuilderPanelPosition,
  getAnnouncementCenterCampaigns,
  getDefaultGuidancePosition,
  isGuidanceFetchUnavailable,
  isGuidanceBuilderMode,
  logGuidanceProgressFailure,
  mountGuidanceBuilder,
  normalizeGuidancePosition,
  renderGuidance,
  resolveCampaigns,
  validateBuilderCampaignPayload,
};
